// Product & Brand Catalog for Supa Energy Hub
// Hierarchy: Category → Brand → Product
// Source: NeoPower-4ppA4-Black-Shield-Brochure-v5-Email.pdf (Feb 2026)

export const NEOPOWER_BROCHURE_URL =
  'https://customer-assets.emergentagent.com/job_abbe48ea-8280-494d-a40e-31241a684337/artifacts/zv7msbj7_NeoPower-4ppA4-Black-Shield-Brochure-v5-Email.pdf';

// Econova does not publish a single brochure PDF for the RVW series; we link to their official product-manuals page.
export const ECONOVA_BROCHURE_URL = 'https://econova.com.au/product-manuals';

// Authentic product imagery sourced from neopower.com.au (with permission)
export const NEOPOWER_IMAGES = {
  productRender: '/product-images/neopower/black-shield-render.png',
  productHero: '/product-images/neopower/black-shield-hero.png',
  shieldLogo: '/product-images/neopower/black-shield-logo.png',
  installations: [
    '/product-images/neopower/install-modern-home.jpg',
    '/product-images/neopower/install-outdoor.jpg',
    '/product-images/neopower/install-wall-mount.jpg',
    '/product-images/neopower/install-backyard.jpg',
  ],
};

export const categories = {
  'heat-pumps': {
    slug: 'heat-pumps',
    name: 'Heat Pumps',
    tagline: 'Energy-efficient electric hot water',
    description:
      'Heat pump hot water systems use up to 75% less energy than conventional electric units by extracting ambient heat. Eligible for federal STCs and state rebates.',
    landingPath: '/heat-pump',
  },
  batteries: {
    slug: 'batteries',
    name: 'Batteries',
    tagline: 'Modular home battery storage',
    description:
      'Store solar by day, run your home by night. Modern lithium-iron-phosphate (LFP) home batteries deliver 100% depth-of-discharge, scalable capacity and seamless solar self-consumption — backed by federal STCs and state battery rebates where eligible.',
    landingPath: '/products/batteries',
  },
};

export const brands = {
  neopower: {
    slug: 'neopower',
    name: 'Neopower',
    seriesName: 'Black Shield',
    established: '2010',
    logo: '/brand-logos/neopower.png',
    category: 'heat-pumps',
    tagline: 'Next Generation Hot Water — All-in-One Heat Pump',
    shortDescription:
      'Neopower delivers premium all-in-one heat pump hot water systems engineered for Australian conditions. The Black Shield range combines a high-COP inverter compressor, R290 natural refrigerant and a smart colour touch screen for unmatched efficiency and longevity.',
    heroEyebrow: 'Next Generation Hot Water',
    pillars: [
      { name: 'Sustainability', icon: 'Leaf' },
      { name: 'Heating', icon: 'Thermometer' },
      { name: 'Innovation', icon: 'Sparkles' },
      { name: 'Efficiency', icon: 'Zap' },
      { name: 'Longevity', icon: 'Shield' },
      { name: 'Dependability', icon: 'Award' },
    ],
    pillarsIntro:
      'Every Black Shield unit is purpose-built for Australian conditions — combining efficiency, intelligence and longevity in a single all-in-one design.',
    installationGalleryTitle: 'Black Shield in Australian Homes',
    installationGalleryIntro:
      'Authentic Neopower installations from across Australia — from compact side passages to modern outdoor walls.',
    whyParagraphs: [
      'Established in 2010, Neopower has built a reputation for delivering next-generation all-in-one heat pump systems that combine engineering rigour with everyday usability.',
      'The Black Shield range is engineered around an advanced inverter compressor that intelligently adjusts to real-time water usage, paired with R290 natural refrigerant for ultra-low environmental impact and a high-grade enamel-coated tank for long-term durability.',
      'When combined with federal STCs and applicable state rebates, Neopower delivers a strong upfront value position for households moving away from gas or aged electric storage.',
    ],
    highlights: [
      'Industry-leading COP 5.4 super efficiency',
      'R290 natural refrigerant — extremely low GWP',
      'Advanced inverter compressor adapts to your usage',
      'Smart colour touch screen + Wi-Fi App control',
      'Four operating modes: Standard, Eco, Hybrid, Element',
      'Anti-corrosion enamel-coated tank with carbon steel',
      'Silent mode 42 dB operation',
      '7 years tank warranty · 5 years compressor*',
    ],
    modelCardPills: ['COP 5.4', 'R290'],
    seo: {
      title: 'Neopower Heat Pump Hot Water — Black Shield Range',
      description:
        'Discover Neopower Black Shield heat pump hot water systems — premium all-in-one units with COP 5.4, R290 refrigerant, smart Wi-Fi app and 320L / 270L tank options. Installed by Supa Energy Hub.',
    },
    installationImages: [
      '/product-images/neopower/install-modern-home.jpg',
      '/product-images/neopower/install-outdoor.jpg',
      '/product-images/neopower/install-wall-mount.jpg',
      '/product-images/neopower/install-backyard.jpg',
    ],
    productRender: '/product-images/neopower/black-shield-render.png',
    brochureUrl:
      'https://customer-assets.emergentagent.com/job_abbe48ea-8280-494d-a40e-31241a684337/artifacts/zv7msbj7_NeoPower-4ppA4-Black-Shield-Brochure-v5-Email.pdf',
    website: 'https://neopower.com.au',
  },
  econova: {
    slug: 'econova',
    name: 'Econova',
    seriesName: 'W-Series RVW',
    established: '2020',
    logo: '/brand-logos/econova.png',
    category: 'heat-pumps',
    tagline: 'Australian-Designed High-Efficiency Heat Pumps',
    shortDescription:
      'Econova is a pioneering Australian-owned designer of one of the most energy-efficient heat pump water heaters on the market. The W-Series RVW range uses R290 natural refrigerant, a DC inverter and variable-speed fan to deliver hot water up to 70°C — efficiently, quietly and sustainably.',
    heroEyebrow: 'Australian Designed Efficiency',
    pillars: [
      { name: 'Efficiency', icon: 'Zap' },
      { name: 'Sustainability', icon: 'Leaf' },
      { name: 'Australian Design', icon: 'MapPin' },
      { name: 'Quiet Operation', icon: 'Volume2' },
      { name: 'Innovation', icon: 'Sparkles' },
      { name: 'Reliability', icon: 'Shield' },
    ],
    pillarsIntro:
      'Econova heat pumps are designed in Australia for Australian conditions — combining the highest efficiency, natural R290 refrigerant and quiet variable-speed operation in a single elegant all-in-one design.',
    installationGalleryTitle: 'Econova Lifestyle & Installations',
    installationGalleryIntro:
      'Authentic Econova imagery — showcasing the W-Series RVW range in real Australian settings.',
    whyParagraphs: [
      'Econova is a pioneering Australian-owned designer and supplier of one of the most energy-efficient heat pump water heaters on the Australian market — dedicated to pushing the boundaries of innovation, sustainability and cost-efficiency at an affordable price.',
      'The W-Series RVW range is engineered around a DC inverter compressor and variable-speed fan that adapt to real heating demand, paired with R290 natural refrigerant for ultra-low environmental impact. Hot-water outlet temperatures reach 70°C, ensuring plenty of hot water any time of day.',
      'Independently tested and certified by a NATA-accredited laboratory, Econova systems are fully compliant with Australian electrical and water safety standards — and qualify for federal STCs plus applicable state rebates.',
    ],
    highlights: [
      'Highest-efficiency variable-speed all-in-one design',
      'COP up to 5.7 — class-leading efficiency',
      'R290 natural refrigerant — ozone-friendly, ultra-low GWP',
      'DC inverter compressor adapts to your usage',
      'Wi-Fi app control — monitor and schedule remotely',
      'Operates from -7°C to +43°C — built for Australian climates',
      'Hot water up to 70°C — plenty for the whole family',
      'Optional 2.0 kW rapid-boost element for cold-zone homes',
    ],
    modelCardPills: ['COP 5.7', 'R290'],
    seo: {
      title: 'Econova Heat Pump Hot Water — W-Series RVW Range',
      description:
        'Discover Econova W-Series RVW heat pump hot water systems — Australian-designed all-in-one units with COP 5.7, R290 refrigerant, Wi-Fi app, 290L tank and optional 2.0 kW rapid-boost element. Installed by Supa Energy Hub.',
    },
    installationImages: [
      '/product-images/econova/install-1.jpg',
      '/product-images/econova/install-2.jpg',
      '/product-images/econova/install-4.jpg',
      '/product-images/econova/install-5.jpg',
    ],
    productRender: '/product-images/econova/rvw-render.png',
    brochureUrl: 'https://econova.com.au/product-manuals',
    website: 'https://econova.com.au',
  },
  midea: {
    slug: 'midea',
    name: 'Midea',
    seriesName: 'Smart Saver',
    established: '1968',
    logo: '/brand-logos/midea.png',
    category: 'heat-pumps',
    tagline: "Australia's #1 Selling Heat Pump Brand — Simply Ideal",
    shortDescription:
      'Midea is a global leader in HVAC and water-heating technology, ranked in the Fortune Global 500 for ten consecutive years. The Smart Saver heat pump range combines R290 natural refrigerant, a wide -20°C to +47°C operating window, automatic disinfection and Wi-Fi control — backed by one of the longest warranties in Australia.',
    heroEyebrow: "Australia's #1 Selling Heat Pump Brand",
    pillars: [
      { name: 'Efficiency', icon: 'Zap' },
      { name: 'Sustainability', icon: 'Leaf' },
      { name: 'Reliability', icon: 'Shield' },
      { name: 'Wide Operating Range', icon: 'Thermometer' },
      { name: 'Smart Control', icon: 'Sparkles' },
      { name: 'Global Trust', icon: 'Award' },
    ],
    pillarsIntro:
      'Midea heat pumps are designed for the most extreme conditions globally — operating from -20°C to +55°C with R290 natural refrigerant, smart Wi-Fi control and an industry-leading 7-year warranty on parts and labour.',
    installationGalleryTitle: 'Midea Smart Saver Range',
    installationGalleryIntro:
      'Authentic Midea heat pump imagery — straight from Midea Australia.',
    whyParagraphs: [
      'Midea Group has entered the Fortune Global 500 for ten consecutive years, ranked #246 in 2025, and is the world\'s number one air-conditioning brand by retail volume (Euromonitor 2020–2023). That global engineering and supply-chain scale flows directly into the Smart Saver heat-pump range you buy in Australia.',
      'The Smart Saver and E-Boost ranges use R290 natural refrigerant for ultra-low Global Warming Potential, an inverter compressor delivering up to COP 4.6, and a wide operating range from -20°C to +47°C (HP + E-Heater). Built-in frost protection, automatic and manual disinfection, Wi-Fi control and solar-PV compatibility are standard.',
      'When combined with federal STCs and applicable state rebates (VEU / ESC), Midea offers exceptional value — and it\'s backed by one of the longest warranties in Australia: 7 years parts and labour on tank and compressor.',
    ],
    highlights: [
      "Australia's #1 selling heat pump brand",
      'R290 natural refrigerant — extremely low GWP',
      'Wide operating range: -20°C to +47°C',
      '7 years parts and labour warranty — one of the longest in Australia',
      'Built-in frost protection + automatic disinfection',
      'Wi-Fi enabled with app control',
      'Solar PV compatible',
      'Eligible for VEU / ESC / STC rebates',
    ],
    modelCardPills: ['COP 4.6', 'R290'],
    seo: {
      title: 'Midea Heat Pump Hot Water — Smart Saver & E-Boost',
      description:
        "Discover Midea's Smart Saver and E-Boost heat pump hot water systems — Australia's #1 selling heat pump brand. COP 4.6, R290 refrigerant, Wi-Fi enabled, 7-year warranty, 280L tank. Installed by Supa Energy Hub.",
    },
    installationImages: [
      '/product-images/midea/heat-pump-1.jpg',
      '/product-images/midea/heat-pump-2.jpg',
      '/product-images/midea/heat-pump-3.jpg',
      '/product-images/midea/brochure-thumb.png',
    ],
    productRender: '/product-images/midea/heat-pump-1.jpg',
    brochureUrl:
      'https://mdhome.com.au/wp-content/uploads/2026/05/MD00210526-Version-Heat-pump-Residential.pdf',
    website: 'https://mdhome.com.au',
  },
  haier: {
    slug: 'haier',
    name: 'Haier',
    seriesName: 'Monoblock HHB',
    established: '1984',
    logo: '/brand-logos/haier.png',
    category: 'heat-pumps',
    tagline: "Inspired Living — Significantly More Efficient Hot Water",
    shortDescription:
      "Haier is one of the world's leading home-appliance manufacturers, empowering smarter, happier and healthier living through innovation. The Haier Monoblock HHB heat-pump water-heater range delivers up to COP 4.48 efficiency with R290 natural refrigerant, SmartHQ™ Wi-Fi control, and patented triple-layer anti-corrosion tank protection.",
    heroEyebrow: 'Inspired Living · Award-Winning Hot Water',
    pillars: [
      { name: 'Smart Living', icon: 'Sparkles' },
      { name: 'Efficiency', icon: 'Zap' },
      { name: 'Sustainability', icon: 'Leaf' },
      { name: 'Quiet Operation', icon: 'Volume2' },
      { name: 'Longevity', icon: 'Shield' },
      { name: 'Global Trust', icon: 'Award' },
    ],
    pillarsIntro:
      'Every Haier Monoblock unit is engineered around six pillars — combining R290 natural refrigerant, SmartHQ™ app control, triple-layer anti-corrosion tank protection and quiet operation in an easy-to-install all-in-one design.',
    installationGalleryTitle: 'Haier Monoblock Heat Pump Gallery',
    installationGalleryIntro:
      'Authentic Haier Monoblock imagery — direct from haierhome.com.au — showcasing the HHB range, R290 refrigerant, anti-corrosion tank and SmartHQ™ wireless control.',
    whyParagraphs: [
      'Haier is a globally recognised home-appliance manufacturer with a customer-centric focus on smart technologies that make life simpler, healthier and more sustainable. Founded in 1984, Haier today operates in over 100 countries and is supported across Australia by a nationwide service network.',
      'The Monoblock HHB range is engineered around R290 natural refrigerant for ultra-low GWP, a triple-layer enamel-protected inner tank for long-term corrosion resistance, and the SmartHQ™ app with five operating modes (Auto, Eco with Solar PV, Electric, Vacation, Boost). Quiet 43–47 dB operation and a wide -7°C to +45°C ambient window make it suitable for almost every Australian climate.',
      'When combined with federal STCs and applicable state rebates (VEU / ESC), Haier offers strong upfront value — backed by 7 years parts on the tank and 5 years parts and labour on the compressor and other major components.',
    ],
    highlights: [
      'COP up to 4.48 — significantly more efficient than electric storage',
      'R290 natural refrigerant — ultra-low GWP of 3',
      'SmartHQ™ Wi-Fi app — 5 operating modes including Solar PV',
      'Patented triple-layer anti-corrosion enamel tank',
      'Quiet operation — from 43 dB(A) at 1m',
      'Wide ambient range: -7°C to +45°C',
      'Easy installation — similar procedure to electric storage',
      'Backed by Haier — global home appliance leader',
    ],
    modelCardPills: ['COP 4.48', 'R290'],
    seo: {
      title: 'Haier Heat Pump Hot Water — Monoblock HHB Range',
      description:
        "Discover Haier Monoblock heat pump hot water systems — HP250 (246L) and HP330 (330L) models with COP up to 4.48, R290 refrigerant, SmartHQ™ app and triple-layer anti-corrosion tank. Installed by Supa Energy Hub.",
    },
    installationImages: [
      '/product-images/haier/monoblock-hero.jpg',
      '/product-images/haier/feature-r290.jpg',
      '/product-images/haier/feature-anticorrosion.jpg',
      '/product-images/haier/feature-wireless.jpg',
    ],
    productRender: '/product-images/haier/hp250-render.png',
    brochureUrl:
      'https://customer-assets.emergentagent.com/job_16c1fb0a-52ec-4e29-9dc1-fc8d06bba0a4/artifacts/mw13jamy_Haier-ProductBrochure-en-HotWater-HVAC-HHB-V8-AU.pdf',
    website: 'https://www.haierhome.com.au',
  },
  emerald: {
    slug: 'emerald',
    name: 'Emerald',
    seriesName: 'All-In-One Pro',
    established: '2006',
    logo: '/brand-logos/emerald.png',
    category: 'heat-pumps',
    tagline: 'Engineered for Effortless Efficiency — Proudly Australian',
    shortDescription:
      "Emerald is an Australian-owned brand engineering hot-water solutions that cost people and the planet less. The All-In-One Pro range combines superior DC technology, R290 natural refrigerant, Wi-Fi app control with Smart Actions and a diamond-enamel-coated carbon-steel tank — designed and tested for Australian conditions since 2006.",
    heroEyebrow: 'Proudly Australian · Engineered for Efficiency',
    pillars: [
      { name: 'Efficiency', icon: 'Zap' },
      { name: 'Australian Design', icon: 'MapPin' },
      { name: 'Smart Control', icon: 'Sparkles' },
      { name: 'Quiet Operation', icon: 'Volume2' },
      { name: 'Sustainability', icon: 'Leaf' },
      { name: 'Reliability', icon: 'Shield' },
    ],
    pillarsIntro:
      "Every Emerald All-In-One Pro unit is engineered around six pillars — combining R290 natural refrigerant, superior DC technology, Wi-Fi app control with Smart Actions and a diamond-enamel tank for everyday performance you can rely on.",
    installationGalleryTitle: 'Emerald in Real Australian Homes',
    installationGalleryIntro:
      'Authentic Emerald imagery — straight from emerald.com.au — showing the All-In-One Pro range alongside rebate-eligible installations across Australia.',
    whyParagraphs: [
      'Emerald is proudly Australian-owned and designed since 2006, with a singular mission: innovate change by engineering hot-water solutions that cost people and the planet less. Every product is technologically superior, lasts longer and performs better — built around efficiency, reliability and smart control.',
      'The All-In-One Pro range is engineered around superior DC technology and R290 natural refrigerant for ultra-low GWP. The diamond-enamel-coated carbon-steel tank delivers long-term corrosion resistance, while built-in Wi-Fi (Emerald App) puts capacity, temperature, usage trends and Boost mode in your pocket — with Smart Actions for fully automated operation.',
      "Whisper-quiet at less than 39 dB(A) at 3 m, eligible for federal STCs and state rebates (VEU / ESC / SRES), and available with an optional 1.6 kW electric backup heater (E variant) for fast recovery in cooler weather — Emerald is built for everyday Australian life.",
    ],
    highlights: [
      'Proudly Australian-owned and designed since 2006',
      'R290 natural refrigerant — ultra-low Global Warming Potential',
      'Superior DC technology for class-leading efficiency',
      'Built-in Wi-Fi + Emerald App with Smart Actions',
      'Real-time tank temperature & capacity monitoring',
      'Whisper-quiet — less than 39 dB(A) at 3 m',
      'Diamond-enamel coated carbon-steel tank',
      'Save up to 80% on hot-water bills*',
    ],
    modelCardPills: ['DC Tech', 'R290'],
    seo: {
      title: 'Emerald Heat Pump Hot Water — All-In-One Pro 320L',
      description:
        "Discover Emerald's All-In-One Pro 320L heat pump hot water systems — proudly Australian since 2006, R290 refrigerant, Wi-Fi app, diamond-enamel tank and ultra-quiet operation. Choose the EE-HWS-A1-320 or the EE-HWS-A1-320E with 1.6 kW backup. Installed by Supa Energy Hub.",
    },
    installationImages: [
      '/product-images/emerald/rebates-1.png',
      '/product-images/emerald/rebates-2.png',
      '/product-images/emerald/rebates-3.png',
      '/product-images/emerald/rebates-4.png',
    ],
    productRender: '/product-images/emerald/all-in-one-render.png',
    brochureUrl:
      'https://customer-assets.emergentagent.com/job_16c1fb0a-52ec-4e29-9dc1-fc8d06bba0a4/artifacts/bwttndda_Emerald-Heat-Pump-All-In-One-brochure.pdf',
    website: 'https://www.emerald.com.au',
  },
  reclaim: {
    slug: 'reclaim',
    name: 'Reclaim Energy',
    seriesName: 'CO2 Heat Pump',
    established: '2014',
    logo: '/brand-logos/reclaim.png',
    category: 'heat-pumps',
    tagline: 'Award-Winning Australian CO2 Heat Pump Hot Water',
    shortDescription:
      'Reclaim Energy is an Australian-owned company committed to energy efficiency, building award-winning CO2 (R744) heat pump hot water systems that deliver up to 80% energy savings — 5× more efficient than conventional electric or gas water heaters. Australian designed for Australian conditions.',
    heroEyebrow: 'Award-Winning · Australian Designed',
    pillars: [
      { name: 'CO2 Refrigerant', icon: 'Leaf' },
      { name: 'Efficiency', icon: 'Zap' },
      { name: 'Australian Design', icon: 'MapPin' },
      { name: 'Smart Controls', icon: 'Sparkles' },
      { name: 'Frost Protection', icon: 'Shield' },
      { name: 'Award-Winning', icon: 'Award' },
    ],
    pillarsIntro:
      "Reclaim Energy CO2 heat pump systems are engineered around six pillars — combining R744 (CO2) natural refrigerant, Wi-Fi controller, built-in frost / freeze / legionella protection and Australian design for energy savings that beat conventional electric and gas hot water.",
    installationGalleryTitle: 'Reclaim Energy Across Australia',
    installationGalleryIntro:
      "Authentic Reclaim Energy imagery — straight from reclaimenergy.com.au — showcasing the CO2 heat pump system in real Australian homes.",
    whyParagraphs: [
      'Reclaim Energy is an Australian-owned company committed to energy efficiency and helping people live more sustainably. The flagship CO2 heat pump hot water system is an innovative, intelligent and customised hot water solution that delivers up to 80% energy savings — 5× more efficient than conventional electric and gas water heaters.',
      'Reclaim systems use CO2 (R744) — a natural refrigerant with a GWP of just 1, the lowest of any commercially viable refrigerant. The split-system design (250L / 315L stainless-steel tank + outdoor heat pump unit) is paired with built-in frost, freeze, and legionella control plus a Wi-Fi controller for full remote programming.',
      "Award-winning since 2014 with 4.9★ Google reviews (148+ reviews), Reclaim is backed by a 6-year tank warranty and excellent Australia-wide service.",
    ],
    highlights: [
      'CO2 (R744) natural refrigerant — GWP of just 1',
      'Up to 80% energy savings vs. electric or gas*',
      'Australian designed and supported since 2014',
      '4.9★ Google rating (148+ reviews)',
      'Built-in frost, freeze and legionella protection',
      'Wi-Fi controller with programmable schedules',
      '316L stainless-steel tank options',
      'Eligible for federal STCs + state rebates',
    ],
    modelCardPills: ['CO2 (R744)', 'Up to 80% savings*'],
    seo: {
      title: 'Reclaim Energy Heat Pump Hot Water — CO2 (R744)',
      description:
        "Discover Reclaim Energy CO2 (R744) heat pump hot water systems — award-winning Australian-designed split units with up to 80% energy savings, built-in frost protection and Wi-Fi controller. Choose the 250L or 315L tank. Installed by Supa Energy Hub.",
    },
    installationImages: [
      '/product-images/reclaim/aio-banner.jpg',
      '/product-images/reclaim/tanks-bg.jpg',
      '/product-images/reclaim/promo.jpg',
      '/product-images/reclaim/find-dealer.jpg',
    ],
    productRender: '/product-images/reclaim/promo.jpg',
    brochureUrl: 'https://reclaimenergy.com.au/products/residential/',
    website: 'https://reclaimenergy.com.au',
  },
  sanden: {
    slug: 'sanden',
    name: 'Sanden',
    seriesName: 'Eco Plus CO2',
    established: '1943',
    logo: '/brand-logos/sanden.svg',
    category: 'heat-pumps',
    tagline: 'Japanese-Engineered CO2 Hot Water — The Gold Standard',
    shortDescription:
      'Sanden is the Japanese global pioneer of CO2 (R744) heat pump technology — co-developer of the original Eco Cute system in 2001. The Eco Plus range delivers exceptional efficiency, near-silent operation and proven reliability that has made it the premium choice for Australian homes seeking the gold standard in heat pump hot water.',
    heroEyebrow: 'The Original CO2 Heat Pump · Since 2001',
    pillars: [
      { name: 'CO2 Refrigerant', icon: 'Leaf' },
      { name: 'Japanese Engineering', icon: 'Award' },
      { name: 'Whisper-Quiet', icon: 'Volume2' },
      { name: 'Durability', icon: 'Shield' },
      { name: 'High Efficiency', icon: 'Zap' },
      { name: 'Global Pioneer', icon: 'Sparkles' },
    ],
    pillarsIntro:
      "Sanden heat pumps are engineered in Japan around six pillars — combining R744 (CO2) natural refrigerant, near-silent operation, precision compressor design and decades of pioneering hot-water engineering.",
    installationGalleryTitle: 'Sanden Eco Plus Range',
    installationGalleryIntro:
      'Authentic Sanden imagery — direct from sanden.com.au — showing the Eco Plus CO2 heat pump in installation contexts.',
    whyParagraphs: [
      "Sanden is a Japanese global manufacturer founded in 1943 with deep heritage in HVAC and refrigeration. As co-developer of the original Eco Cute CO2 heat pump system launched in Japan in 2001, Sanden is widely considered the pioneer of natural-refrigerant heat-pump hot water — the technology now used globally to reduce GWP impact.",
      'The Eco Plus range available in Australia is a split-system design (separate compact outdoor heat pump and indoor or outdoor stainless-steel tank) using CO2 (R744) — a natural refrigerant with GWP of 1. Hot-water outlet temperatures reach 65°C with low ambient operation down to -10°C.',
      'Sanden systems are backed by Japanese precision engineering, 15-year tank life expectancy and an industry-leading 6-year tank warranty — combined with whisper-quiet 37 dB operation.',
    ],
    highlights: [
      'Co-developer of the original CO2 heat-pump system (2001)',
      'CO2 (R744) natural refrigerant — GWP of just 1',
      'Whisper-quiet 37 dB(A) operation',
      'Japanese precision engineering',
      'Stainless-steel tank — 15-year design life',
      'Operates down to -10°C ambient',
      'High outlet temperature (65°C)',
      'Eligible for federal STCs + state rebates',
    ],
    modelCardPills: ['CO2 (R744)', '37 dB Quiet'],
    seo: {
      title: 'Sanden Eco Plus Heat Pump Hot Water — CO2 (R744)',
      description:
        "Discover Sanden Eco Plus heat pump hot water systems — Japanese-engineered CO2 (R744) split systems with whisper-quiet 37 dB operation, 65°C outlet, stainless-steel tank. Choose the 250L (GAUS-A45EQTA) or 315L (GAUS-A45HPS). Installed by Supa Energy Hub.",
    },
    installationImages: [
      '/product-images/sanden/banner1.png',
      '/product-images/sanden/thumbnail-1.png',
      '/product-images/sanden/thumbnail-1-1.png',
      '/product-images/sanden/thumbnail-1-2.png',
    ],
    productRender: '/product-images/sanden/banner1.png',
    brochureUrl: 'https://sanden.com.au/',
    website: 'https://sanden.com.au',
  },
  istore: {
    slug: 'istore',
    name: 'iStore',
    seriesName: 'iStore Hot Water',
    established: '2010',
    logo: '/brand-logos/istore.png',
    category: 'heat-pumps',
    tagline: 'Australian-Designed Energy Storage and Hot Water',
    shortDescription:
      'iStore is an Australian-designed range of energy products focused on home electrification — from heat pump hot water to pool heaters, battery storage and EV chargers. The iStore Hot Water range delivers compact, all-in-one heat pump efficiency with Aussie-tuned controls and a robust enamel-coated tank.',
    heroEyebrow: 'Australian Designed · Built for Electrification',
    pillars: [
      { name: 'Australian Design', icon: 'MapPin' },
      { name: 'Compact Footprint', icon: 'Shield' },
      { name: 'Smart Control', icon: 'Sparkles' },
      { name: 'Quiet Operation', icon: 'Volume2' },
      { name: 'Efficiency', icon: 'Zap' },
      { name: 'Sustainability', icon: 'Leaf' },
    ],
    pillarsIntro:
      'iStore Hot Water units are engineered for Australian electrification — compact all-in-one heat pumps with smart controls, enamel-coated tanks and rebate-eligible specifications.',
    installationGalleryTitle: 'iStore Across the Home',
    installationGalleryIntro:
      'Authentic iStore imagery — direct from istore.net.au — showcasing the full energy-product range.',
    whyParagraphs: [
      'iStore is an Australian-designed range of products that helps households fully electrify their homes — combining hot water heat pumps, pool heat pumps, battery storage and EV chargers in one ecosystem. The iStore Hot Water range is purpose-built for Australian conditions.',
      'The 180L and 270L Hot Water models are all-in-one heat pumps with robust enamel-coated tanks, Wi-Fi app control, and smart scheduling for solar PV alignment. Compact footprint suits homes where space is at a premium.',
      'Combined with federal STCs and applicable state rebates, iStore offers strong value for households moving away from electric or gas storage — backed by an Aussie warranty and a growing nationwide installer network.',
    ],
    highlights: [
      'Proudly Australian-designed product range',
      'Compact all-in-one design — minimal footprint',
      'Wi-Fi app with solar-PV scheduling',
      'Enamel-coated steel tank for durability',
      'Quiet outdoor operation',
      'Eligible for VEU / ESC / STC rebates',
      'Part of a wider home-electrification ecosystem',
      'Australian nationwide installer network',
    ],
    modelCardPills: ['Aussie Designed', 'Compact'],
    seo: {
      title: 'iStore Heat Pump Hot Water — 180L and 270L',
      description:
        "Discover iStore Hot Water heat pump systems — Australian-designed compact all-in-one units in 180L and 270L tank sizes with Wi-Fi app, enamel-coated tank, and rebate-eligible specifications. Installed by Supa Energy Hub.",
    },
    installationImages: [
      '/product-images/istore/180l-menu.webp',
      '/product-images/istore/270l-menu.webp',
      '/product-images/istore/banner.jpg',
      '/product-images/istore/nav-bg.jpg',
    ],
    productRender: '/product-images/istore/270l-menu.webp',
    brochureUrl: 'https://istore.net.au/',
    website: 'https://istore.net.au',
  },
  aquatech: {
    slug: 'aquatech',
    name: 'Aquatech',
    seriesName: 'Rapid Heat Pump',
    established: '1992',
    logo: '/brand-logos/aquatech.png',
    category: 'heat-pumps',
    tagline: 'Australian Hot Water Specialists — Rapid Recovery, Quiet Operation',
    shortDescription:
      "Aquatech is an Australian hot water specialist with decades of experience in commercial and residential water systems. The Rapid heat-pump range delivers fast hot-water recovery, low-noise operation and efficient performance — suited to growing Australian households seeking reliable, sustainable hot water.",
    heroEyebrow: 'Australian Hot Water Specialists',
    pillars: [
      { name: 'Rapid Recovery', icon: 'Zap' },
      { name: 'Quiet Operation', icon: 'Volume2' },
      { name: 'Australian Made', icon: 'MapPin' },
      { name: 'Reliability', icon: 'Shield' },
      { name: 'Efficiency', icon: 'Sparkles' },
      { name: 'Sustainability', icon: 'Leaf' },
    ],
    pillarsIntro:
      "Aquatech Rapid heat pumps are engineered around six pillars — combining fast recovery, low-noise operation, sustainable refrigerants and Australian-specialist design.",
    installationGalleryTitle: 'Aquatech Rapid Heat Pump',
    installationGalleryIntro:
      'Authentic Aquatech imagery showcasing the Rapid heat-pump range and its modern, quiet operation.',
    whyParagraphs: [
      "Aquatech is an Australian hot-water specialist that has been engineering commercial and residential water solutions since the early 1990s — with a focus on reliability, performance and customer service.",
      "The Rapid range is purpose-built for households that prioritise fast hot-water recovery and quiet operation. With efficient inverter compressors and low-GWP refrigerant, the Rapid range delivers consistent hot water with minimal noise.",
      'Backed by a national service network and rebate-eligible specifications, Aquatech offers Australian-specialist support for households moving from electric or gas storage.',
    ],
    highlights: [
      'Australian hot-water specialist since 1992',
      'Rapid hot-water recovery',
      'Low-noise operation',
      'Inverter compressor',
      'Eligible for federal STCs + state rebates',
      'National service network',
      'Robust, commercial-grade engineering',
      'Wi-Fi-enabled smart control (select models)',
    ],
    modelCardPills: ['Rapid Recovery', 'Quiet'],
    seo: {
      title: 'Aquatech Heat Pump Hot Water — Rapid Range',
      description:
        "Discover Aquatech Rapid heat pump hot water systems — Australian hot-water specialists with fast recovery, low-noise operation and rebate-eligible specs in 250L and 300L tank sizes. Installed by Supa Energy Hub.",
    },
    installationImages: [
      '/product-images/aquatech/228c32_de03ca3b6ed34607b0f7f9fc4f3ba889.png',
      '/product-images/aquatech/228c32_77455d10a51241759093564322608c00.png',
      '/product-images/aquatech/228c32_ccf8694eb15143d988a3e40acaa0cee3.png',
    ],
    productRender: '/product-images/aquatech/228c32_de03ca3b6ed34607b0f7f9fc4f3ba889.png',
    brochureUrl: 'https://aquatechheatpumps.com.au/',
    website: 'https://aquatechheatpumps.com.au',
  },
  stiebel: {
    slug: 'stiebel',
    name: 'Stiebel Eltron',
    seriesName: 'WWK Series',
    established: '1924',
    logo: '/brand-logos/stiebel.svg',
    category: 'heat-pumps',
    tagline: 'German-Engineered Hot Water — Precision Since 1924',
    shortDescription:
      "Stiebel Eltron is a German manufacturer with over 100 years of heritage in electric water heating, hot-water heat pumps and electric space heating. The WWK series of integrated heat-pump water heaters delivers German precision engineering, refined controls and proven reliability — distributed across Australia from Sydney.",
    heroEyebrow: 'German Engineered · Since 1924',
    pillars: [
      { name: 'German Engineering', icon: 'Award' },
      { name: 'Precision', icon: 'Sparkles' },
      { name: 'Efficiency', icon: 'Zap' },
      { name: 'Sustainability', icon: 'Leaf' },
      { name: 'Reliability', icon: 'Shield' },
      { name: 'Heritage', icon: 'MapPin' },
    ],
    pillarsIntro:
      "Stiebel Eltron WWK heat pump water heaters are engineered in Germany around six pillars — combining precision, reliability, efficiency and over a century of hot-water heritage.",
    installationGalleryTitle: 'Stiebel Eltron Heritage',
    installationGalleryIntro:
      'Stiebel Eltron — German engineered since 1924. Integrated heat-pump water heaters distributed across Australia from Sydney.',
    whyParagraphs: [
      "Stiebel Eltron is a German company founded in 1924, recognised globally as one of the most experienced manufacturers of electric water heating, hot-water heat pumps, and electric space heating. Its products are sold in over 120 countries.",
      "The WWK range is a fully integrated heat-pump water heater — combining a high-COP heat-pump compressor, refined intuitive controls, frost protection and a corrosion-resistant tank in a single all-in-one design. Available in 222L (WWK 222) and 302L (WWK 302) tank sizes.",
      "Backed by Stiebel Eltron's Australian distributor (Sydney-based) with a national service network, the WWK series suits households that value German precision and longevity.",
    ],
    highlights: [
      'Over 100 years of hot-water heritage (1924)',
      'German engineered and manufactured',
      'Integrated heat-pump water heater design',
      'Refined intuitive controls with frost protection',
      'Corrosion-resistant tank',
      'Sold in over 120 countries',
      'Sydney-based Australian distributor',
      'Eligible for federal STCs + state rebates',
    ],
    modelCardPills: ['German Eng.', 'Since 1924'],
    seo: {
      title: 'Stiebel Eltron Heat Pump Hot Water — WWK Series',
      description:
        "Discover Stiebel Eltron WWK series heat pump hot water systems — German-engineered integrated heat-pump water heaters since 1924, available in 222L and 302L tank sizes. Installed by Supa Energy Hub.",
    },
    installationImages: [],
    productRender: '/brand-logos/stiebel.svg',
    brochureUrl: 'https://www.stiebel-eltron.com.au/',
    website: 'https://www.stiebel-eltron.com.au',
  },
  'fox-ess': {
    slug: 'fox-ess',
    name: 'Fox ESS',
    seriesName: 'CQ + EQ High-Voltage Series',
    established: '2019',
    logo: '/brand-logos/fox-ess.png',
    category: 'batteries',
    tagline: 'High-voltage modular home battery storage',
    shortDescription:
      'Fox ESS engineers high-voltage lithium-iron-phosphate (LFP) home battery systems with a modular, plug-and-play design. The EQ4800, CQ6 and CQ7 ranges deliver 100% usable energy, IP65 outdoor-ready protection and scalability from a single 4.7 kWh module up to a stacked 97.4 kWh wall — for everything from compact townhouses to large family homes with three-phase loads.',
    heroEyebrow: 'High-Voltage Home Battery',
    pillars: [
      { name: 'Modular', icon: 'Battery' },
      { name: 'High Voltage', icon: 'Zap' },
      { name: 'Scalable', icon: 'Maximize2' },
      { name: 'Safety', icon: 'Shield' },
      { name: 'High Efficiency', icon: 'Sparkles' },
      { name: 'Outdoor IP65', icon: 'Award' },
    ],
    pillarsIntro:
      'Fox ESS batteries are engineered around six core pillars — every CQ and EQ module pairs LFP cell safety with plug-and-play installation, scalable energy and high-voltage efficiency for Australian homes.',
    installationGalleryTitle: 'Fox ESS in Australian Homes',
    installationGalleryIntro:
      'Real-world Fox ESS battery installations across Australian homes — clean, wall-mounted stacks paired with Fox ESS hybrid inverters in garages and external alcoves.',
    whyParagraphs: [
      'Fox ESS is a global energy-storage and inverter manufacturer with strong distribution and after-sales support across Australia, and a product portfolio purpose-built for the local single- and three-phase residential market.',
      'Their CQ and EQ batteries use LFP (LiFePO4) chemistry — the safest mainstream lithium battery format — combined with a fully modular high-voltage architecture. You stack modules to reach the energy you need today, and add more later as solar generation or EV charging grows.',
      'With IP65 outdoor protection, broad operating-temperature tolerance and a published inverter-compatibility matrix, Fox ESS batteries integrate cleanly with leading hybrid inverters and are well suited to the Australian federal Cheaper Home Batteries Program and applicable state battery rebates*.',
    ],
    highlights: [
      'LFP (LiFePO4) cell chemistry — safest lithium format',
      '100% usable depth-of-discharge across all models',
      'IP65 outdoor-rated — flexible install locations',
      '>95% round-trip efficiency',
      'Modular plug-and-play stack design',
      'Scalable from 4.7 kWh up to 97.4 kWh*',
      'Wide -10 °C to 55 °C discharge operating range',
      'IEC 62619 + UN38.3 + EN 61000 EMC certified',
    ],
    modelCardPills: ['LFP Chemistry', 'IP65'],
    seo: {
      title: 'Fox ESS Batteries — EQ4800, CQ6 & CQ7 Home Storage',
      description:
        'Fox ESS EQ4800, CQ6 and CQ7 high-voltage modular home batteries — LFP chemistry, 100% DOD, IP65 outdoor-rated, scalable from 4.7 kWh to 97.4 kWh. Installed by Supa Energy Hub.',
    },
    installationImages: [
      '/product-images/fox-ess/eq4800-install-1.jpg',
      '/product-images/fox-ess/eq4800-install-2.jpg',
      '/product-images/fox-ess/eq4800-install-3.jpg',
      '/product-images/fox-ess/eq4800-install-4.jpg',
    ],
    productRender: '/product-images/fox-ess/battery-hero.jpg',
    brochureUrl: 'https://au.fox-ess.com/Public/Uploads/uploadfile/files/20251219/ENCQ6DatasheetAUV1.220251118.pdf',
    website: 'https://au.fox-ess.com',
  },
  whes: {
    slug: 'whes',
    name: 'WHES',
    seriesName: 'Power Pod Agave Series',
    established: '2010',
    logo: 'https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/vziwce9r_logo-WHESS.png',
    category: 'batteries',
    tagline: 'All-in-one residential battery storage — single & three-phase',
    shortDescription:
      'WHES (Weiheng) engineers all-in-one residential battery energy storage systems used in 50+ countries. The Power Pod Agave Series ships a hybrid inverter, LFP battery modules and EMS in a single 600 mm-wide cabinet — available as a single-phase (AGAVE-SH Ultra, 3.6–10 kW · up to 19.9 kWh) and three-phase (AGAVE-TH, 5–13 kW · up to 29.9 kWh) variant, both built around the same BXC4992 battery module.',
    heroEyebrow: 'All-In-One Energy Storage',
    pillars: [
      { name: 'All-In-One', icon: 'Battery' },
      { name: 'LFP Safety', icon: 'Shield' },
      { name: 'Fast EPS', icon: 'Zap' },
      { name: '<25 dB Quiet', icon: 'Volume2' },
      { name: 'IP65 Outdoor', icon: 'Award' },
      { name: '5-Unit Parallel', icon: 'Maximize2' },
    ],
    pillarsIntro:
      'WHES Power Pod Agave systems integrate a hybrid inverter and stackable LFP batteries into a single all-in-one cabinet — engineered around six pillars that make residential storage simpler, safer and quieter.',
    installationGalleryTitle: 'WHES Power Pod Agave in Australian Homes',
    installationGalleryIntro:
      'Compact all-in-one cabinets that integrate hybrid inverter, LFP battery modules and EMS — installed against an external wall in 15-minute stackable modules.',
    whyParagraphs: [
      'WHES (Weiheng) is a leading global residential and commercial battery energy storage manufacturer, supplying customers in 50+ countries with comprehensive design-to-deployment ESS solutions.',
      'The Power Pod Agave family compresses a hybrid inverter, LFP (LiFePO4) battery modules and EMS into a single all-in-one cabinet — saving install time, simplifying the AC/DC wiring footprint and dropping straight onto an external wall. The SH Ultra single-phase model scales 1–4 modules (4.99–19.97 kWh), and the TH three-phase model scales 2–6 modules (9.98–29.94 kWh). Both share the same 4.992 kWh BXC4992 module so installers can mix-and-match stock.',
      'Both systems are IP65 outdoor-rated, run at <25 dB ambient (whisper-quiet), include a built-in fire-suppression kit, ship with UL9540A-certified cells and are AS/NZS 4777.2 grid-compliant — making them well-suited to the Australian federal Cheaper Home Batteries Program and applicable state battery rebates*.',
    ],
    highlights: [
      'LFP (LiFePO4) battery chemistry',
      'BXC4992 modular battery — 4.992 kWh per module',
      'Single-phase 3.6–10 kW · Three-phase 5–13 kW',
      'Scalable 4.99 kWh → up to 149.76 kWh (5-unit parallel)',
      '<10 ms EPS backup switch-over',
      'Integrated fire-suppression kit',
      'IP65 outdoor-rated · <25 dB ambient',
      '10-year product + performance warranty*',
    ],
    modelCardPills: ['LFP Chemistry', 'IP65'],
    seo: {
      title: 'WHES Power Pod Agave — Single & Three-Phase Home Batteries',
      description:
        'WHES Power Pod Agave SH Ultra (single-phase, 3.6–10 kW · 4.99–19.9 kWh) and AGAVE-TH (three-phase, 5–13 kW · 9.98–29.94 kWh) all-in-one home battery storage systems — LFP chemistry, IP65, 10-year warranty. Installed by Supa Energy Hub.',
    },
    installationImages: [
      '/product-images/whes/agave-th-hero.png',
      '/product-images/whes/agave-sh-render.png',
      '/product-images/whes/agave-th-reliability.png',
      '/product-images/whes/agave-th-protection.jpg',
    ],
    productRender: '/product-images/whes/agave-th-render.png',
    brochureUrl: '/brochures/whes/whes-agave-sh-datasheet.pdf',
    warrantyUrl: '/brochures/whes/whes-residential-warranty-au.pdf',
    website: 'https://www.whes.com/au',
  },
  ecactus: {
    slug: 'ecactus',
    name: 'eCactus',
    seriesName: 'Agave SH All-in-One',
    established: '2010',
    logo: 'https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/vziwce9r_logo-WHESS.png',
    category: 'batteries',
    tagline: 'Residential all-in-one battery storage by Weiheng',
    shortDescription:
      'eCactus is the residential brand of Weiheng — a global battery energy storage manufacturer supplying 50+ countries. The Agave SH all-in-one packs a hybrid inverter, LFP battery modules, EMS and a built-in fire-suppression kit into one IP65 outdoor-rated cabinet — built for Australian single-phase homes.',
    heroEyebrow: 'All-in-One Residential ESS',
    pillars: [
      { name: 'All-in-One', icon: 'Battery' },
      { name: 'LFP Safety', icon: 'Shield' },
      { name: 'Fast EPS <10 ms', icon: 'Zap' },
      { name: '<25 dB Quiet', icon: 'Volume2' },
      { name: 'IP65 Outdoor', icon: 'Award' },
      { name: 'Scalable to 51 kWh', icon: 'Maximize2' },
    ],
    pillarsIntro:
      'eCactus Agave SH systems integrate a hybrid inverter and stackable LFP batteries into a single all-in-one cabinet — engineered to be safe, silent and seriously efficient.',
    installationGalleryTitle: 'eCactus Agave SH in Australian Homes',
    installationGalleryIntro:
      'Compact 600 mm-wide all-in-one cabinets that integrate hybrid inverter, LFP modules and EMS — designed to disappear into the side of your home.',
    whyParagraphs: [
      'eCactus is the residential brand of Weiheng, a global battery energy storage manufacturer with comprehensive design-to-deployment ESS solutions used in 50+ countries.',
      'The Agave SH all-in-one (model WH-SPHA5.0H-10.24kWh) combines a 5 kW hybrid inverter with stackable LFP battery modules — 10.24 kWh per stack, scalable up to 5 cabinets in parallel for around 51 kWh of usable energy. A built-in fire-suppression kit, four-layer safety design and UL9540A-certified cells make it one of the safest single-phase ESS cabinets on the Australian market.',
      'IP65 outdoor-rated, <25 dB ambient, AS/NZS 4777.2 grid-compliant — well-suited to the Australian federal Cheaper Home Batteries Program and applicable state battery rebates*.',
    ],
    highlights: [
      'LFP (LiFePO4) battery chemistry',
      '10.24 kWh per stack · scalable to ~51 kWh',
      '5 kW single-phase hybrid inverter',
      '<10 ms EPS backup switch-over',
      'Integrated fire-suppression kit',
      'IP65 outdoor-rated · <25 dB ambient',
      '10-year product + performance warranty*',
      'AS/NZS 4777.2 + UL9540A certified',
    ],
    modelCardPills: ['LFP Chemistry', 'IP65'],
    seo: {
      title: 'eCactus Agave SH — All-in-One Home Battery Storage',
      description:
        'eCactus Agave SH single-phase all-in-one residential battery (5 kW · 10.24 kWh, scalable). LFP chemistry, IP65 outdoor, 10-year warranty. Installed by Supa Energy Hub.',
    },
    installationImages: [
      '/product-images/whes/agave-th-hero.png',
      '/product-images/whes/agave-sh-render.png',
      '/product-images/whes/agave-th-reliability.png',
      '/product-images/whes/agave-th-protection.jpg',
    ],
    productRender: '/product-images/whes/agave-sh-render.png',
    brochureUrl: '/brochures/ecactus/ecactus-agave-sh-datasheet.pdf',
    warrantyUrl: '/brochures/ecactus/ecactus-aio-warranty-au.pdf',
    website: 'https://www.ecactus.com',
  },
};

// Each product belongs to one category and one brand.
export const products = {
  'neopower-na45-320b': {
    slug: 'neopower-na45-320b',
    category: 'heat-pumps',
    brand: 'neopower',
    series: 'Black Shield',
    model: 'NA45-320B',
    name: 'Neopower Black Shield 320L',
    image: '/product-images/neopower/black-shield-render.png',
    lifestyleImage: '/product-images/neopower/install-modern-home.jpg',
    heroPills: [
      { icon: 'Zap', label: 'COP 5.4' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Volume2', label: '42 dB Silent Mode' },
      { icon: 'Wifi', label: 'Wi-Fi + App' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '320',
      unit: 'L',
    },
    tagline: 'Large-family hot water with super-efficient COP 5.4 performance.',
    description:
      'The Black Shield NA45-320B is a 320-litre all-in-one heat pump hot water system ideal for households of 4–6 people. Featuring an advanced inverter compressor, R290 natural refrigerant and a smart colour touch screen, it delivers exceptional efficiency, ultra-quiet operation and STC-eligible savings upfront.',
    heroBadge: 'Best for 4–6 person households',
    keyFeatures: [
      { title: 'Super Efficiency COP 5.4', detail: 'Industry-leading coefficient of performance.' },
      { title: 'Advanced Inverter Compressor', detail: 'Adjusts operating frequency to real-time water usage.' },
      { title: 'Smart Colour Touch Screen', detail: 'Intuitive on-unit controls and diagnostics.' },
      { title: 'R290 Natural Refrigerant', detail: 'Extremely low Global Warming Potential.' },
      { title: 'Anti-Corrosion Enamel Tank', detail: 'Extra-thick carbon steel for longevity.' },
      { title: 'Integrated Wi-Fi + App', detail: 'Monitor and control remotely from anywhere.' },
      { title: 'Four Smart Modes', detail: 'Standard · Eco · Hybrid · Heating Element.' },
      { title: 'Silent Mode', detail: 'Whisper-quiet 42 dB low-noise operation.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '320 L' },
      { label: 'Rated Heating Capacity', value: '2.9 kW' },
      { label: 'Rated Input Power', value: '0.537 kW' },
      { label: 'COP', value: '5.4' },
      { label: 'Refrigerant', value: 'R290' },
      { label: 'Max Water Temperature', value: '75 °C' },
      { label: 'Ambient Operating Range', value: '-7 °C to 46 °C' },
      { label: 'Noise Level', value: '42–48 dB (mode dependent)' },
      { label: 'Voltage', value: '220–240 V / 50–60 Hz' },
      { label: 'Height', value: '2,090 mm' },
      { label: 'Diameter', value: '650 mm' },
      { label: 'Weight (Net / Gross)', value: '146 kg / 166 kg' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years' },
      { component: 'Compressor', term: '5 years' },
      { component: 'Labour', term: '2 / 5 years*' },
    ],
    rebateEligible: true,
  },
  'neopower-na44-270b': {
    slug: 'neopower-na44-270b',
    category: 'heat-pumps',
    brand: 'neopower',
    series: 'Black Shield',
    model: 'NA44-270B',
    name: 'Neopower Black Shield 270L',
    image: '/product-images/neopower/black-shield-render.png',
    lifestyleImage: '/product-images/neopower/install-outdoor.jpg',
    heroPills: [
      { icon: 'Zap', label: 'COP 5.4' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Volume2', label: '42 dB Silent Mode' },
      { icon: 'Wifi', label: 'Wi-Fi + App' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '270',
      unit: 'L',
    },
    tagline: 'Compact powerhouse for medium households — COP 5.4 efficiency.',
    description:
      'The Black Shield NA44-270B is a 270-litre all-in-one heat pump hot water system designed for households of 3–5 people. Same premium Black Shield technology — advanced inverter compressor, R290 refrigerant, smart Wi-Fi app and four operating modes — in a more compact footprint.',
    heroBadge: 'Best for 3–5 person households',
    keyFeatures: [
      { title: 'Super Efficiency COP 5.4', detail: 'Industry-leading coefficient of performance.' },
      { title: 'Advanced Inverter Compressor', detail: 'Adjusts operating frequency to real-time water usage.' },
      { title: 'Smart Colour Touch Screen', detail: 'Intuitive on-unit controls and diagnostics.' },
      { title: 'R290 Natural Refrigerant', detail: 'Extremely low Global Warming Potential.' },
      { title: 'Anti-Corrosion Enamel Tank', detail: 'Extra-thick carbon steel for longevity.' },
      { title: 'Integrated Wi-Fi + App', detail: 'Monitor and control remotely from anywhere.' },
      { title: 'Four Smart Modes', detail: 'Standard · Eco · Hybrid · Heating Element.' },
      { title: 'Silent Mode', detail: 'Whisper-quiet 42 dB low-noise operation.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '270 L' },
      { label: 'Rated Heating Capacity', value: '3.0 kW' },
      { label: 'Rated Input Power', value: '0.556 kW' },
      { label: 'COP', value: '5.4' },
      { label: 'Refrigerant', value: 'R290' },
      { label: 'Max Water Temperature', value: '75 °C' },
      { label: 'Ambient Operating Range', value: '-7 °C to 46 °C' },
      { label: 'Noise Level', value: '42–48 dB (mode dependent)' },
      { label: 'Voltage', value: '220–240 V / 50–60 Hz' },
      { label: 'Height', value: '1,870 mm' },
      { label: 'Diameter', value: '650 mm' },
      { label: 'Weight (Net / Gross)', value: '130 kg / 150 kg' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years' },
      { component: 'Compressor', term: '5 years' },
      { component: 'Labour', term: '2 / 5 years*' },
    ],
    rebateEligible: true,
  },
  'econova-300rvw': {
    slug: 'econova-300rvw',
    category: 'heat-pumps',
    brand: 'econova',
    series: 'W-Series RVW',
    model: 'ECON-300RVW',
    name: 'Econova W-Series 300L (RVW)',
    image: '/product-images/econova/rvw-render.png',
    lifestyleImage: '/product-images/econova/install-1.jpg',
    heroPills: [
      { icon: 'Zap', label: 'COP up to 5.7' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Wifi', label: 'Wi-Fi + App' },
      { icon: 'Volume2', label: 'Variable-Speed Fan' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '290',
      unit: 'L',
    },
    tagline: 'Highest-efficiency variable-speed all-in-one — quiet, clean and Australian-designed.',
    description:
      'The ECON-300RVW is Econova\'s flagship all-in-one heat pump hot water system — a DC-inverter variable-speed unit delivering hot water up to 70°C with COP 5.7 and R290 natural refrigerant. Designed in Australia for Australian conditions, with Wi-Fi app control and ultra-low noise operation.',
    heroBadge: 'Best for 3–5 person households',
    keyFeatures: [
      { title: 'COP up to 5.7', detail: 'Class-leading coefficient of performance.' },
      { title: 'DC Inverter Variable Speed', detail: 'Compressor adapts to real-time hot-water demand.' },
      { title: 'R290 Natural Refrigerant', detail: 'Ozone-friendly, ultra-low Global Warming Potential.' },
      { title: 'Wi-Fi App Control', detail: 'Remote monitoring and scheduling from your phone.' },
      { title: 'Variable-Speed Fan', detail: 'Smart airflow for whisper-quiet operation.' },
      { title: 'Up to 70°C Outlet Temp', detail: 'Hot water whenever you need it — day or night.' },
      { title: 'Wide Operating Range', detail: 'Operates from -7°C to +43°C ambient temperatures.' },
      { title: 'Australian Designed', detail: 'Engineered for Australian climates and conditions.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '290 L' },
      { label: 'Rated Heating Capacity', value: '2.65 kW' },
      { label: 'COP', value: 'Up to 5.7' },
      { label: 'Refrigerant', value: 'R290 (Natural)' },
      { label: 'Max Water Outlet Temperature', value: '70 °C' },
      { label: 'Ambient Operating Range', value: '-7 °C to +43 °C' },
      { label: 'Rapid Boost Element', value: '0 kW (no booster element)' },
      { label: 'Variable Speed Fan', value: 'Yes' },
      { label: 'Wi-Fi Control', value: 'Yes (app)' },
      { label: 'Dimension (H × W)', value: '2,005 × 640 mm' },
      { label: 'Compressor', value: 'DC Inverter' },
      { label: 'Configuration', value: 'All-in-one (integrated tank + heat pump)' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years*' },
      { component: 'Compressor', term: '5 years*' },
      { component: 'Labour', term: '2 years*' },
    ],
    rebateEligible: true,
  },
  'econova-300rvw-2e': {
    slug: 'econova-300rvw-2e',
    category: 'heat-pumps',
    brand: 'econova',
    series: 'W-Series RVW',
    model: 'ECON-300RVW-2.0E',
    name: 'Econova W-Series 300L with 2.0 kW Booster (RVW-2.0E)',
    image: '/product-images/econova/rvw-render.png',
    lifestyleImage: '/product-images/econova/install-2.jpg',
    heroPills: [
      { icon: 'Zap', label: 'COP up to 5.7' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Wifi', label: 'Wi-Fi + App' },
      { icon: 'Volume2', label: '2.0 kW Boost Element' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '290',
      unit: 'L',
    },
    tagline: 'Same high-efficiency RVW unit plus a 2.0 kW rapid-boost element for fastest winter recovery.',
    description:
      'The ECON-300RVW-2.0E pairs Econova\'s flagship 290L variable-speed heat pump with a 2.0 kW rapid-boost electric element — ideal for larger households or homes in colder climate zones that need fast recovery during peak hot-water demand. Same COP 5.7 efficiency, R290 refrigerant and Wi-Fi app control.',
    heroBadge: 'Best for 4–6 person households · colder zones',
    keyFeatures: [
      { title: '2.0 kW Rapid Boost Element', detail: 'Fast-recovery electric booster for peak demand or cold days.' },
      { title: 'COP up to 5.7', detail: 'Class-leading coefficient of performance in heat-pump mode.' },
      { title: 'DC Inverter Variable Speed', detail: 'Compressor adapts to real-time hot-water demand.' },
      { title: 'R290 Natural Refrigerant', detail: 'Ozone-friendly, ultra-low Global Warming Potential.' },
      { title: 'Wi-Fi App Control', detail: 'Remote monitoring and scheduling from your phone.' },
      { title: 'Variable-Speed Fan', detail: 'Smart airflow for whisper-quiet operation.' },
      { title: 'Up to 70°C Outlet Temp', detail: 'Hot water whenever you need it — day or night.' },
      { title: 'Wide Operating Range', detail: 'Operates from -7°C to +43°C ambient temperatures.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '290 L' },
      { label: 'Rated Heating Capacity', value: '2.65 kW' },
      { label: 'COP', value: 'Up to 5.7' },
      { label: 'Refrigerant', value: 'R290 (Natural)' },
      { label: 'Max Water Outlet Temperature', value: '70 °C' },
      { label: 'Ambient Operating Range', value: '-7 °C to +43 °C' },
      { label: 'Rapid Boost Element', value: '2.0 kW (electric booster)' },
      { label: 'Variable Speed Fan', value: 'Yes' },
      { label: 'Wi-Fi Control', value: 'Yes (app)' },
      { label: 'Dimension (H × W)', value: '2,005 × 640 mm' },
      { label: 'Compressor', value: 'DC Inverter' },
      { label: 'Configuration', value: 'All-in-one with 2.0 kW boost element' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years*' },
      { component: 'Compressor', term: '5 years*' },
      { component: 'Labour', term: '2 years*' },
    ],
    rebateEligible: true,
  },
  'econova-330frec-wr': {
    slug: 'econova-330frec-wr',
    category: 'heat-pumps',
    brand: 'econova',
    series: 'W-Series Split',
    model: 'ECON-330FREC-WR',
    name: 'Econova Split 324L (ECON-330FREC-WR)',
    image: '/product-images/econova/econ-330frec-wr-render.jpg',
    lifestyleImage: '/product-images/econova/install-4.jpg',
    heroVariant: 'light',
    heroStatCard: {
      label: 'Tank Warranty',
      value: '6',
      unit: '-yr',
      sub: 'Welded enamel tank · magnesium anode*',
      icon: 'Shield',
    },
    heroChip: { label: 'R290 · GWP 3', icon: 'Leaf' },
    heroPills: [
      { icon: 'Zap', label: 'COP 4.9' },
      { icon: 'Leaf', label: 'R290 · GWP 3' },
      { icon: 'Wifi', label: 'Wi-Fi + App' },
      { icon: 'Volume2', label: '4.8 kW Rapid Boost' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '324',
      unit: 'L',
    },
    tagline: 'Large-family split-system heat pump — separate outdoor compressor + 324 L tank, 4.8 kW rapid boost.',
    description:
      'The ECON-330FREC-WR is Econova\'s split-system heat pump water heater — a separate outdoor compressor unit paired with a 324-litre Obsidian Grey indoor tank. Designed for larger Australian households (3–7 people) and demanding installations, it combines a 4.998 kW R290 heat pump (COP 4.9) with a powerful 4.8 kW rapid-boost electric element for fast peak-demand recovery, plus a >350 L first-hour delivery and 99 L/hr recovery rate for back-to-back showers. Wi-Fi app control, automatic reverse-cycle defrost, magnesium-rod anode and a fully welded enamel tank built to Australian standards.',
    heroBadge: 'Best for 3–7 person households · split system',
    keyFeatures: [
      { title: 'Split-System Design', detail: 'Separate outdoor heat-pump unit + indoor tank — flexible install layouts, easier servicing.' },
      { title: '4.998 kW Heating Capacity', detail: 'Strong heat-pump output for fast tank reheats.' },
      { title: 'COP 4.9', detail: 'High coefficient of performance with R290 natural refrigerant.' },
      { title: '350+ L First-Hour Delivery', detail: 'Hot water for back-to-back showers and large baths.' },
      { title: '99 L/hr Recovery Rate', detail: 'Quick reheating between peak-demand periods.' },
      { title: '4.8 kW Rapid Boost Element', detail: 'Powerful electric booster for high demand or extreme cold.' },
      { title: 'Wi-Fi App Control', detail: 'Remote monitoring, scheduling, ECO and Auto modes from your phone.' },
      { title: 'R290 Natural Refrigerant', detail: 'GWP of just 3 — among the lowest of any hot-water refrigerant.' },
      { title: 'Automatic Defrost', detail: 'Reverse-cycle defrost keeps the outdoor unit efficient even at low ambients.' },
      { title: 'Up to 65°C Outlet Temp', detail: 'Plenty of hot water at safe-tap temperatures.' },
      { title: 'Wide Operating Range', detail: 'Operates from −7 °C to +43 °C ambient.' },
      { title: 'Welded Enamel Tank', detail: 'Built to Australian standards with magnesium-rod corrosion protection.' },
    ],
    specs: [
      { label: 'Configuration', value: 'Split system (separate outdoor compressor + indoor tank)' },
      { label: 'Tank Capacity', value: '324 L (storage)' },
      { label: 'Rated Heating Capacity', value: '4.998 kW' },
      { label: 'COP', value: '4.9 W/W' },
      { label: 'Power Input', value: '1,020 W' },
      { label: 'Rapid Boost Element', value: '4.8 kW (electric booster)' },
      { label: 'First-Hour Delivery', value: '>350 L' },
      { label: 'Recovery Rate', value: '99 L/hr' },
      { label: 'Refrigerant', value: 'R290 (Natural · GWP 3)' },
      { label: 'Max Water Outlet Temperature', value: '60 °C / 65 °C boost' },
      { label: 'Ambient Operating Range', value: '−7 °C to +43 °C' },
      { label: 'Indoor Sound Level', value: '48 dB' },
      { label: 'Wi-Fi Control', value: 'Yes (app)' },
      { label: 'Defrost', value: 'Reverse-cycle (automatic)' },
      { label: 'Dimensions (H × W × D)', value: '1,815 × 620 × 620 mm' },
      { label: 'Weight', value: '116 kg' },
      { label: 'Ingress Protection', value: 'IPX4' },
      { label: 'Max Water Pressure', value: '850 kPa (PTR relief valve)' },
      { label: 'Tank Construction', value: 'Welded enamel · Magnesium rod anode' },
      { label: 'Finish', value: 'Obsidian Grey' },
      { label: 'Electrical Connection', value: 'Hardwired · 220–240 V / 50 Hz' },
      { label: 'Country of Origin', value: 'China' },
    ],
    warranty: [
      { component: 'Tank', term: '6 years*' },
      { component: 'Heat Pump (rest of split system)', term: '5 years*' },
      { component: 'Labour & Ancillary Parts', term: '2 years*' },
    ],
    rebateEligible: true,
  },
  'midea-smart-saver-280': {
    slug: 'midea-smart-saver-280',
    category: 'heat-pumps',
    brand: 'midea',
    series: 'Smart Saver',
    model: 'RSJ-23/300RDN7-L2',
    name: 'Midea Smart Saver 280L (RSJ-23/300RDN7-L2)',
    image: '/product-images/midea/heat-pump-1.jpg',
    lifestyleImage: '/product-images/midea/heat-pump-2.jpg',
    heroPills: [
      { icon: 'Zap', label: 'COP 4.6' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Wifi', label: 'Wi-Fi + App' },
      { icon: 'Volume2', label: '48 dB Operation' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '280',
      unit: 'L',
    },
    tagline: "Australia's #1 selling heat pump brand — Simply Ideal for the modern Australian home.",
    description:
      "The Midea Smart Saver RSJ-23/300RDN7-L2 is a 280-litre all-in-one heat pump hot water system from Australia's #1 selling heat pump brand. Featuring R290 natural refrigerant, COP 4.6 efficiency, Wi-Fi app control, automatic disinfection and a wide operating range — backed by one of the longest warranties in Australia.",
    heroBadge: 'Best for 4–5 person households',
    keyFeatures: [
      { title: 'Smart Heat Pump Technology', detail: '400% efficiency — 4× more efficient than a traditional electric water heater.' },
      { title: 'R290 Natural Refrigerant', detail: 'Ultra-low Global Warming Potential — environmentally friendly.' },
      { title: 'Wi-Fi Control + App', detail: 'Monitor and schedule hot water from your phone.' },
      { title: 'ECONOMY / Hybrid / E-Heater Modes', detail: 'Three operating modes optimised for cost or speed.' },
      { title: 'Wide Operating Range', detail: '-20°C to +47°C with built-in frost protection.' },
      { title: 'Auto + Manual Disinfection', detail: 'Two-way disinfection cycle for safer water.' },
      { title: '10 AMP Plug', detail: 'Plug-and-play installation — no special wiring required.' },
      { title: 'Solar PV Compatible', detail: 'Pair with rooftop solar to maximise savings.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '280 L' },
      { label: 'Rated Heating Capacity (HP only)', value: '2.5 kW' },
      { label: 'COP', value: '4.6' },
      { label: 'Refrigerant', value: 'R290 / 0.42 kg (GWP: 3)' },
      { label: 'Max Water Outlet Temperature', value: '70 °C (default 60 °C)' },
      { label: 'Ambient Range (HP only)', value: '-7 °C to +43 °C' },
      { label: 'Ambient Range (HP + E-Heater)', value: '-20 °C to +45 °C' },
      { label: 'Noise Level', value: '48 dB(A)' },
      { label: 'Voltage / Frequency', value: '1 Ph / 220–240 V / 50 Hz' },
      { label: 'Max Current', value: '9 A (10 AMP plug)' },
      { label: 'Dimensions (D × W × H)', value: '650 × 650 × 1,962 mm' },
      { label: 'Weight (Net / Gross)', value: '138 kg / 170 kg' },
      { label: 'Compressor', value: 'Inverter' },
      { label: 'Configuration', value: 'All-in-one (no booster element)' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years*' },
      { component: 'Compressor', term: '7 years*' },
      { component: 'Labour', term: '7 years*' },
    ],
    rebateEligible: true,
  },
  'midea-eboost-280': {
    slug: 'midea-eboost-280',
    category: 'heat-pumps',
    brand: 'midea',
    series: 'Smart Saver E-Boost',
    model: 'RSJ-23/300RDN7-L2-D',
    name: 'Midea E-Boost 280L (RSJ-23/300RDN7-L2-D)',
    image: '/product-images/midea/heat-pump-1.jpg',
    lifestyleImage: '/product-images/midea/heat-pump-3.jpg',
    heroPills: [
      { icon: 'Zap', label: 'COP 4.6' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Wifi', label: 'Wi-Fi + App' },
      { icon: 'Volume2', label: '4.1 kW Boost Element' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '280',
      unit: 'L',
    },
    tagline: 'Same Smart Saver platform plus a 4.1 kW rapid-boost element — for fast recovery in larger households or colder climates.',
    description:
      "The Midea E-Boost RSJ-23/300RDN7-L2-D pairs the 280L Smart Saver heat pump with a 4.1 kW electric rapid-boost element — ideal for larger households or colder climate zones that need fast recovery during peak hot-water demand. Same COP 4.6 efficiency, R290 refrigerant and 7-year warranty on parts and labour.",
    heroBadge: 'Best for 5–6 person households · colder zones',
    keyFeatures: [
      { title: '4.1 kW Rapid Boost Element', detail: 'Fast-recovery electric booster for peak demand or cold days.' },
      { title: 'Smart Heat Pump Technology', detail: '400% efficiency — 4× more efficient than a traditional electric water heater.' },
      { title: 'R290 Natural Refrigerant', detail: 'Ultra-low Global Warming Potential — environmentally friendly.' },
      { title: 'Wi-Fi Control + App', detail: 'Monitor and schedule hot water from your phone.' },
      { title: 'ECONOMY / Hybrid / E-Heater Modes', detail: 'Three operating modes — balance efficiency with recovery speed.' },
      { title: 'Wide Operating Range', detail: '-20°C to +45°C with built-in frost protection (HP + E-Heater).' },
      { title: 'Auto + Manual Disinfection', detail: 'Two-way disinfection cycle for safer water.' },
      { title: 'Solar PV Compatible', detail: 'Pair with rooftop solar to maximise savings.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '280 L' },
      { label: 'Rated Heating Capacity (HP only)', value: '2.5 kW' },
      { label: 'Rapid Boost Element', value: '4.1 kW (electric booster)' },
      { label: 'COP', value: '4.6' },
      { label: 'Refrigerant', value: 'R290 / 0.42 kg (GWP: 3)' },
      { label: 'Max Water Outlet Temperature', value: '70 °C (default 65 °C)' },
      { label: 'Ambient Range (HP only)', value: '-7 °C to +43 °C' },
      { label: 'Ambient Range (HP + E-Heater)', value: '-20 °C to +45 °C' },
      { label: 'Noise Level', value: '48 dB(A)' },
      { label: 'Voltage / Frequency', value: '1 Ph / 220–240 V / 50 Hz' },
      { label: 'Max Current', value: '20.7 A' },
      { label: 'Dimensions (D × W × H)', value: '650 × 650 × 1,962 mm' },
      { label: 'Weight (Net / Gross)', value: '140 kg / 172 kg' },
      { label: 'Configuration', value: 'All-in-one with 4.1 kW E-Boost element' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years*' },
      { component: 'Compressor', term: '7 years*' },
      { component: 'Labour', term: '7 years*' },
    ],
    rebateEligible: true,
  },
  'haier-hp250m1-u1': {
    slug: 'haier-hp250m1-u1',
    category: 'heat-pumps',
    brand: 'haier',
    series: 'Monoblock HHB',
    model: 'HP250M1-U1',
    name: 'Haier Monoblock 250L (HP250M1-U1)',
    image: '/product-images/haier/hp250-render.png',
    lifestyleImage: '/product-images/haier/monoblock-hero.jpg',
    heroPills: [
      { icon: 'Zap', label: 'COP 4.48' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Wifi', label: 'SmartHQ™ App' },
      { icon: 'Volume2', label: '43 dB Quiet' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '246',
      unit: 'L',
    },
    tagline: 'Inspired Living — significantly more efficient than a traditional electric water heater.',
    description:
      "The Haier HP250M1-U1 is a 246-litre Monoblock heat pump water heater delivering COP 4.48 efficiency with R290 natural refrigerant. Featuring SmartHQ™ app control with 5 operating modes (Auto, Eco with Solar PV, Electric, Vacation, Boost), a patented triple-layer enamel tank, and whisper-quiet 43 dB operation. Easy to install — same procedure as a standard electric storage water heater.",
    heroBadge: 'Best for 3–4 person households',
    keyFeatures: [
      { title: 'COP 4.48 Efficiency', detail: 'Significantly more efficient than traditional electric water heaters.' },
      { title: 'R290 Natural Refrigerant', detail: 'GWP of just 3 — ultra-low environmental impact.' },
      { title: 'SmartHQ™ Wi-Fi App', detail: 'Auto, Eco with Solar PV, Electric, Vacation and Boost modes.' },
      { title: 'Triple-Layer Anti-Corrosion Tank', detail: 'Patented enamel protection for long-term durability.' },
      { title: '43 dB Quiet Operation', detail: 'Whisper-quiet at 1 metre — perfect for residential settings.' },
      { title: 'Solar PV Ready', detail: 'Pair with rooftop solar to maximise free hot water.' },
      { title: '1.5 kW Electric Backup', detail: 'Built-in element for fast recovery on cold days.' },
      { title: 'Easy Monoblock Installation', detail: 'Same install procedure as an electric storage unit.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '246 L' },
      { label: 'Rated Heating Capacity (HP only)', value: '2.0 kW' },
      { label: 'Rated Power Input (HP)', value: '0.43 kW' },
      { label: 'Maximum Power Input (HP)', value: '0.75 kW' },
      { label: 'Maximum Power Input (Appliance)', value: '2.25 kW' },
      { label: 'COP', value: '4.48' },
      { label: 'Refrigerant', value: 'R290 / 0.34 kg (GWP: 3)' },
      { label: 'Max Water Outlet Temperature', value: '75 °C' },
      { label: 'Heating Range (HP only)', value: '35 °C – 65 °C' },
      { label: 'Ambient Operating Range', value: '-7 °C to +45 °C' },
      { label: 'Noise Level (at 1m)', value: '43 dB(A)' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Electric Backup Element', value: '1.5 kW' },
      { label: 'Configuration', value: 'Monoblock — all-in-one' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years parts*' },
      { component: 'Compressor', term: '5 years parts & labour*' },
      { component: 'Labour', term: '5 years*' },
    ],
    rebateEligible: true,
  },
  'haier-hp330m1u1': {
    slug: 'haier-hp330m1u1',
    category: 'heat-pumps',
    brand: 'haier',
    series: 'Monoblock HHB',
    model: 'HP330M1U1',
    name: 'Haier Monoblock 330L (HP330M1U1)',
    image: '/product-images/haier/hp330-render.jpg',
    lifestyleImage: '/product-images/haier/monoblock-hero.jpg',
    heroPills: [
      { icon: 'Zap', label: 'COP 4.0' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Wifi', label: 'SmartHQ™ App' },
      { icon: 'Volume2', label: '47 dB Quiet' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '330',
      unit: 'L',
    },
    tagline: 'Inspired Living for larger households — 330L Monoblock with R290 and SmartHQ™ control.',
    description:
      "The Haier HP330M1U1 is a 330-litre Monoblock heat pump water heater built for larger households or homes with high hot-water demand. Same award-winning Monoblock platform — R290 refrigerant, SmartHQ™ Wi-Fi app, triple-layer anti-corrosion tank — with increased 2.8 kW heating capacity and a 2.2 kW electric backup element for fast recovery.",
    heroBadge: 'Best for 5–7 person households',
    keyFeatures: [
      { title: '2.8 kW Heating Capacity', detail: 'Larger compressor sized for high hot-water demand.' },
      { title: 'COP 4.0', detail: 'Significantly more efficient than electric storage water heaters.' },
      { title: 'R290 Natural Refrigerant', detail: 'GWP of just 3 — ultra-low environmental impact.' },
      { title: 'SmartHQ™ Wi-Fi App', detail: 'Auto, Eco with Solar PV, Electric, Vacation and Boost modes.' },
      { title: 'Triple-Layer Anti-Corrosion Tank', detail: 'Patented enamel protection for long-term durability.' },
      { title: '2.2 kW Electric Backup', detail: 'Larger booster element for fast recovery on cold days.' },
      { title: 'Quiet 47 dB Operation', detail: 'Low-noise outdoor operation at 1 metre.' },
      { title: 'Solar PV Ready', detail: 'Pair with rooftop solar to maximise free hot water.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '330 L' },
      { label: 'Rated Heating Capacity (HP only)', value: '2.8 kW' },
      { label: 'Rated Power Input (HP)', value: '0.7 kW' },
      { label: 'Maximum Power Input (HP)', value: '1.2 kW' },
      { label: 'Maximum Power Input (Appliance)', value: '3.4 kW' },
      { label: 'COP', value: '4.00' },
      { label: 'Refrigerant', value: 'R290 / 0.47 kg (GWP: 3)' },
      { label: 'Max Water Outlet Temperature', value: '75 °C' },
      { label: 'Heating Range (HP only)', value: '35 °C – 65 °C' },
      { label: 'Ambient Operating Range', value: '-7 °C to +45 °C' },
      { label: 'Noise Level (at 1m)', value: '47 dB(A)' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Electric Backup Element', value: '2.2 kW' },
      { label: 'Configuration', value: 'Monoblock — all-in-one' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years parts*' },
      { component: 'Compressor', term: '5 years parts & labour*' },
      { component: 'Labour', term: '5 years*' },
    ],
    rebateEligible: true,
  },
  'emerald-ee-hws-a1-320': {
    slug: 'emerald-ee-hws-a1-320',
    category: 'heat-pumps',
    brand: 'emerald',
    series: 'All-In-One Pro',
    model: 'EE-HWS-A1-320',
    name: 'Emerald All-In-One Pro 320L (EE-HWS-A1-320)',
    image: '/product-images/emerald/all-in-one-render.png',
    lifestyleImage: '/product-images/emerald/rebates-1.png',
    heroPills: [
      { icon: 'Zap', label: 'Superior DC Tech' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Wifi', label: 'Emerald App + Smart Actions' },
      { icon: 'Volume2', label: '< 39 dB(A) at 3m' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '320',
      unit: 'L',
    },
    tagline: 'Proudly Australian All-In-One Pro 320L — engineered for effortless efficiency.',
    description:
      "The Emerald EE-HWS-A1-320 is a 320-litre All-In-One Pro heat pump hot water system designed in Australia since 2006. Featuring superior DC technology, R290 natural refrigerant, built-in Wi-Fi with the Emerald App and Smart Actions, and a diamond-enamel-coated carbon-steel tank — built to deliver peak performance for homes and businesses alike.",
    heroBadge: 'Best for 4–6 person households · efficient heat-pump only',
    keyFeatures: [
      { title: 'Superior DC Technology', detail: 'Class-leading efficiency and long-term reliability.' },
      { title: 'R290 Natural Refrigerant', detail: 'Gentle on the environment — ultra-low Global Warming Potential.' },
      { title: 'Emerald App + Smart Actions', detail: 'Built-in Wi-Fi for automation, scheduling and Boost mode.' },
      { title: 'Real-Time Tank Monitoring', detail: 'Tank temperature, capacity and usage trends — straight from your phone.' },
      { title: 'Diamond Enamel Carbon-Steel Tank', detail: 'Durable, corrosion-resistant for long service life.' },
      { title: 'Whisper-Quiet Operation', detail: 'Less than 39 dB(A) at 3 metres.' },
      { title: 'Modern Compact Design', detail: 'Slim 600 mm diameter — fits where others don\'t.' },
      { title: 'Save up to 80% on Hot-Water Bills*', detail: 'vs. conventional electric storage water heaters.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '320 L' },
      { label: 'Compressor Technology', value: 'Superior DC' },
      { label: 'Refrigerant', value: 'R290 (Natural)' },
      { label: 'Refrigerant GWP', value: 'Low (R290 ≈ 3)' },
      { label: 'Noise Level', value: '< 39 dB(A) at 3 m' },
      { label: 'Wi-Fi / App Control', value: 'Yes — Emerald App' },
      { label: 'Smart Actions', value: 'Yes — automated operation' },
      { label: 'Electric Backup Element', value: 'N/A (heat-pump only)' },
      { label: 'Dimensions (Ø × H)', value: '600 × 2,050 mm' },
      { label: 'Net Weight', value: '146 kg' },
      { label: 'Gross Weight', value: '154 kg' },
      { label: 'Tank Material', value: 'Carbon steel with diamond enamel coating' },
      { label: 'Configuration', value: 'All-in-one (integrated tank + heat pump)' },
      { label: 'Country of Design', value: 'Australia (since 2006)' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years*' },
      { component: 'Compressor', term: '5 years*' },
      { component: 'Labour', term: 'Per Emerald T&Cs*' },
    ],
    rebateEligible: true,
  },
  'emerald-ee-hws-a1-320e': {
    slug: 'emerald-ee-hws-a1-320e',
    category: 'heat-pumps',
    brand: 'emerald',
    series: 'All-In-One Pro',
    model: 'EE-HWS-A1-320E',
    name: 'Emerald All-In-One Pro 320L with 1.6 kW Backup (EE-HWS-A1-320E)',
    image: '/product-images/emerald/all-in-one-render.png',
    lifestyleImage: '/product-images/emerald/rebates-2.png',
    heroPills: [
      { icon: 'Zap', label: 'Superior DC Tech' },
      { icon: 'Leaf', label: 'R290 Refrigerant' },
      { icon: 'Wifi', label: 'Emerald App + Smart Actions' },
      { icon: 'Volume2', label: '1.6 kW Backup' },
    ],
    headlineSpec: {
      label: 'Tank Size',
      value: '320',
      unit: 'L',
    },
    tagline: 'Same All-In-One Pro 320L platform plus a 1.6 kW electric backup heater — for fast recovery in cooler weather.',
    description:
      "The Emerald EE-HWS-A1-320E pairs the proudly-Australian 320L All-In-One Pro heat pump with a 1.6 kW electric backup heater — engineered for households or businesses that need fast recovery during peak demand or cooler weather. Same superior DC technology, R290 refrigerant, Emerald App and diamond-enamel tank.",
    heroBadge: 'Best for 4–6 person households · cooler zones / high demand',
    keyFeatures: [
      { title: '1.6 kW Electric Backup Heater', detail: 'Fast-recovery booster element for cooler weather and peak demand.' },
      { title: 'Superior DC Technology', detail: 'Class-leading efficiency and long-term reliability.' },
      { title: 'R290 Natural Refrigerant', detail: 'Gentle on the environment — ultra-low GWP.' },
      { title: 'Emerald App + Smart Actions', detail: 'Built-in Wi-Fi for automation, scheduling and Boost mode.' },
      { title: 'Real-Time Tank Monitoring', detail: 'Tank temperature, capacity and usage trends from your phone.' },
      { title: 'Diamond Enamel Carbon-Steel Tank', detail: 'Durable, corrosion-resistant for long service life.' },
      { title: 'Whisper-Quiet Operation', detail: 'Less than 39 dB(A) at 3 metres.' },
      { title: 'Save up to 80% on Hot-Water Bills*', detail: 'vs. conventional electric storage water heaters.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '320 L' },
      { label: 'Compressor Technology', value: 'Superior DC' },
      { label: 'Electric Backup Element', value: '1.6 kW' },
      { label: 'Refrigerant', value: 'R290 (Natural)' },
      { label: 'Refrigerant GWP', value: 'Low (R290 ≈ 3)' },
      { label: 'Noise Level', value: '< 39 dB(A) at 3 m' },
      { label: 'Wi-Fi / App Control', value: 'Yes — Emerald App' },
      { label: 'Smart Actions', value: 'Yes — automated operation' },
      { label: 'Dimensions (Ø × H)', value: '600 × 2,050 mm' },
      { label: 'Net Weight', value: '146 kg' },
      { label: 'Gross Weight', value: '154 kg' },
      { label: 'Tank Material', value: 'Carbon steel with diamond enamel coating' },
      { label: 'Configuration', value: 'All-in-one with 1.6 kW electric backup' },
      { label: 'Country of Design', value: 'Australia (since 2006)' },
    ],
    warranty: [
      { component: 'Tank', term: '7 years*' },
      { component: 'Compressor', term: '5 years*' },
      { component: 'Labour', term: 'Per Emerald T&Cs*' },
    ],
    rebateEligible: true,
  },
  'reclaim-co2-250': {
    slug: 'reclaim-co2-250',
    category: 'heat-pumps',
    brand: 'reclaim',
    series: 'CO2 Heat Pump',
    model: 'REHP-CO2-250SST',
    name: 'Reclaim Energy CO2 250L (REHP-CO2-250SST)',
    image: '/product-images/reclaim/promo.jpg',
    lifestyleImage: '/product-images/reclaim/tanks-bg.jpg',
    heroPills: [
      { icon: 'Leaf', label: 'CO2 (R744) Refrigerant' },
      { icon: 'Zap', label: 'Up to 80% Savings*' },
      { icon: 'Wifi', label: 'Wi-Fi Controller' },
      { icon: 'Volume2', label: 'Frost & Legionella Protection' },
    ],
    headlineSpec: { label: 'Tank Size', value: '250', unit: 'L' },
    tagline: 'Award-winning Australian CO2 heat pump — up to 80% energy savings, 5× more efficient than conventional water heaters.',
    description:
      "The Reclaim Energy REHP-CO2-250SST is a 250-litre split-system CO2 (R744) heat pump hot water system — Australian-designed, award-winning, and one of the most efficient heat pumps on the market. Pair the 250L stainless-steel tank with the outdoor heat-pump unit, programme via the Wi-Fi controller, and enjoy built-in frost, freeze and legionella protection.",
    heroBadge: 'Best for 3–4 person households',
    keyFeatures: [
      { title: 'CO2 (R744) Natural Refrigerant', detail: 'Lowest possible GWP of 1 — the most environmentally friendly refrigerant available.' },
      { title: 'Up to 80% Energy Savings*', detail: '5× more efficient than conventional electric or gas water heaters.' },
      { title: 'Wi-Fi Controller', detail: 'Programmable schedules with One-Shot Boost from anywhere.' },
      { title: '316L Stainless-Steel Tank', detail: 'Corrosion-resistant tank built for 15+ years of service.' },
      { title: 'Built-In Frost Protection', detail: 'Operates reliably down to low ambient temperatures.' },
      { title: 'Legionella Cycle', detail: 'Automated weekly disinfection cycle for safer water.' },
      { title: 'Australian Designed', detail: 'Engineered for Australian conditions since 2014.' },
      { title: 'Award-Winning', detail: '4.9★ Google rating, multiple industry awards.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '250 L' },
      { label: 'Refrigerant', value: 'CO2 / R744 (GWP: 1)' },
      { label: 'Heat Pump Output', value: '4.5 kW' },
      { label: 'COP', value: 'Up to 5.0' },
      { label: 'Tank Material', value: '316L Stainless Steel' },
      { label: 'Max Water Outlet Temperature', value: '63 °C' },
      { label: 'Ambient Operating Range', value: '-10 °C to +43 °C' },
      { label: 'Noise Level', value: '~37 dB(A)' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Frost / Legionella Protection', value: 'Yes (built-in)' },
      { label: 'Wi-Fi Controller', value: 'Yes' },
      { label: 'Configuration', value: 'Split system (outdoor HP + tank)' },
      { label: 'Country of Design', value: 'Australia (since 2014)' },
    ],
    warranty: [
      { component: 'Tank', term: '6 years*' },
      { component: 'Compressor', term: '5 years*' },
      { component: 'Labour', term: 'Per Reclaim T&Cs*' },
    ],
    rebateEligible: true,
  },
  'reclaim-co2-315': {
    slug: 'reclaim-co2-315',
    category: 'heat-pumps',
    brand: 'reclaim',
    series: 'CO2 Heat Pump',
    model: 'REHP-CO2-315SST',
    name: 'Reclaim Energy CO2 315L (REHP-CO2-315SST)',
    image: '/product-images/reclaim/promo.jpg',
    lifestyleImage: '/product-images/reclaim/aio-banner.jpg',
    heroPills: [
      { icon: 'Leaf', label: 'CO2 (R744) Refrigerant' },
      { icon: 'Zap', label: 'Up to 80% Savings*' },
      { icon: 'Wifi', label: 'Wi-Fi Controller' },
      { icon: 'Volume2', label: '315 L · Larger Households' },
    ],
    headlineSpec: { label: 'Tank Size', value: '315', unit: 'L' },
    tagline: 'Larger 315L CO2 heat pump for bigger households — same award-winning Australian engineering.',
    description:
      "The Reclaim Energy REHP-CO2-315SST is a 315-litre version of the award-winning CO2 (R744) heat pump platform — designed for larger households or homes with higher hot-water demand. Same Australian-engineered split-system, Wi-Fi controller, 316L stainless-steel tank and built-in frost/legionella protection — just sized bigger.",
    heroBadge: 'Best for 5–7 person households',
    keyFeatures: [
      { title: '315 L Stainless-Steel Tank', detail: 'Larger capacity for high hot-water demand households.' },
      { title: 'CO2 (R744) Natural Refrigerant', detail: 'Lowest possible GWP of 1.' },
      { title: 'Up to 80% Energy Savings*', detail: '5× more efficient than conventional water heaters.' },
      { title: 'Wi-Fi Controller', detail: 'Programmable schedules + One-Shot Boost.' },
      { title: 'Built-In Frost Protection', detail: 'Operates reliably down to low ambient temperatures.' },
      { title: 'Legionella Cycle', detail: 'Automated weekly disinfection cycle.' },
      { title: 'Australian Designed', detail: 'Engineered for Australian conditions since 2014.' },
      { title: '4.9★ Google Rating', detail: 'Award-winning service backed by 148+ reviews.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '315 L' },
      { label: 'Refrigerant', value: 'CO2 / R744 (GWP: 1)' },
      { label: 'Heat Pump Output', value: '4.5 kW' },
      { label: 'COP', value: 'Up to 5.0' },
      { label: 'Tank Material', value: '316L Stainless Steel' },
      { label: 'Max Water Outlet Temperature', value: '63 °C' },
      { label: 'Ambient Operating Range', value: '-10 °C to +43 °C' },
      { label: 'Noise Level', value: '~37 dB(A)' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Frost / Legionella Protection', value: 'Yes (built-in)' },
      { label: 'Wi-Fi Controller', value: 'Yes' },
      { label: 'Configuration', value: 'Split system (outdoor HP + tank)' },
      { label: 'Country of Design', value: 'Australia (since 2014)' },
    ],
    warranty: [
      { component: 'Tank', term: '6 years*' },
      { component: 'Compressor', term: '5 years*' },
      { component: 'Labour', term: 'Per Reclaim T&Cs*' },
    ],
    rebateEligible: true,
  },
  'sanden-eco-plus-250': {
    slug: 'sanden-eco-plus-250',
    category: 'heat-pumps',
    brand: 'sanden',
    series: 'Eco Plus',
    model: 'GAUS-A45EQTA',
    name: 'Sanden Eco Plus 250L (GAUS-A45EQTA)',
    image: '/product-images/sanden/banner1.png',
    lifestyleImage: '/product-images/sanden/thumbnail-1.png',
    heroPills: [
      { icon: 'Leaf', label: 'CO2 (R744) Refrigerant' },
      { icon: 'Zap', label: 'Japanese Engineering' },
      { icon: 'Volume2', label: '37 dB Whisper-Quiet' },
      { icon: 'Wifi', label: 'Pioneer Since 2001' },
    ],
    headlineSpec: { label: 'Tank Size', value: '250', unit: 'L' },
    tagline: 'Japanese-engineered CO2 heat pump — the original gold standard since 2001.',
    description:
      "The Sanden Eco Plus GAUS-A45EQTA is a 250-litre split-system CO2 (R744) heat pump hot water system from the Japanese pioneer of natural-refrigerant heat-pump technology. Featuring a precision Japanese compressor, whisper-quiet 37 dB operation, and a stainless-steel tank with a 15-year design life.",
    heroBadge: 'Best for 3–4 person households',
    keyFeatures: [
      { title: 'Sanden CO2 (R744) Pioneer', detail: 'Co-developed the original Eco Cute heat pump system in 2001.' },
      { title: 'Japanese Precision Engineering', detail: 'Compressor and controls engineered in Japan.' },
      { title: 'Whisper-Quiet 37 dB(A)', detail: 'Among the quietest heat pumps in Australia.' },
      { title: 'Stainless-Steel Tank', detail: '15-year design life with industry-leading warranty.' },
      { title: '65 °C Outlet Temperature', detail: 'Plenty of hot water any time of day.' },
      { title: 'Low Ambient Operation', detail: 'Operates down to -10 °C ambient.' },
      { title: 'GWP of Just 1', detail: 'CO2 refrigerant — the most environmentally friendly available.' },
      { title: 'Split-System Design', detail: 'Compact outdoor heat-pump unit + indoor/outdoor tank.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '250 L' },
      { label: 'Refrigerant', value: 'CO2 / R744 (GWP: 1)' },
      { label: 'Rated Heating Capacity', value: '4.5 kW' },
      { label: 'COP', value: 'Up to 5.0' },
      { label: 'Tank Material', value: 'Stainless Steel' },
      { label: 'Max Water Outlet Temperature', value: '65 °C' },
      { label: 'Ambient Operating Range', value: '-10 °C to +43 °C' },
      { label: 'Noise Level', value: '37 dB(A)' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Configuration', value: 'Split system (outdoor HP + tank)' },
      { label: 'Country of Origin', value: 'Japan' },
      { label: 'Heritage', value: 'CO2 heat pump pioneer (since 2001)' },
    ],
    warranty: [
      { component: 'Tank', term: '6 years parts*' },
      { component: 'Compressor', term: '6 years parts*' },
      { component: 'Labour', term: 'Per Sanden T&Cs*' },
    ],
    rebateEligible: true,
  },
  'sanden-eco-plus-315': {
    slug: 'sanden-eco-plus-315',
    category: 'heat-pumps',
    brand: 'sanden',
    series: 'Eco Plus',
    model: 'GAUS-A45HPS',
    name: 'Sanden Eco Plus 315L (GAUS-A45HPS)',
    image: '/product-images/sanden/banner1.png',
    lifestyleImage: '/product-images/sanden/thumbnail-1-1.png',
    heroPills: [
      { icon: 'Leaf', label: 'CO2 (R744) Refrigerant' },
      { icon: 'Zap', label: 'Japanese Engineering' },
      { icon: 'Volume2', label: '37 dB Whisper-Quiet' },
      { icon: 'Wifi', label: '315 L · Larger Households' },
    ],
    headlineSpec: { label: 'Tank Size', value: '315', unit: 'L' },
    tagline: 'Larger 315L CO2 heat pump for bigger Australian families — same Japanese-engineered platform.',
    description:
      "The Sanden Eco Plus GAUS-A45HPS is the 315-litre version of Sanden's flagship split-system CO2 (R744) heat pump — sized for larger Australian households. Same Japanese precision compressor, whisper-quiet 37 dB operation, and stainless-steel tank — just with more capacity for higher hot-water demand.",
    heroBadge: 'Best for 5–7 person households',
    keyFeatures: [
      { title: '315 L Stainless-Steel Tank', detail: 'Larger capacity for high hot-water demand households.' },
      { title: 'Japanese Precision Engineering', detail: 'Compressor and controls engineered in Japan.' },
      { title: 'Whisper-Quiet 37 dB(A)', detail: 'Among the quietest heat pumps in Australia.' },
      { title: 'CO2 (R744) Natural Refrigerant', detail: 'Lowest GWP of 1.' },
      { title: '65 °C Outlet Temperature', detail: 'Plenty of hot water any time of day.' },
      { title: 'Low Ambient Operation', detail: 'Operates down to -10 °C ambient.' },
      { title: '15-Year Tank Design Life', detail: 'Industry-leading durability.' },
      { title: 'Split-System Design', detail: 'Compact outdoor heat-pump unit + indoor/outdoor tank.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '315 L' },
      { label: 'Refrigerant', value: 'CO2 / R744 (GWP: 1)' },
      { label: 'Rated Heating Capacity', value: '4.5 kW' },
      { label: 'COP', value: 'Up to 5.0' },
      { label: 'Tank Material', value: 'Stainless Steel' },
      { label: 'Max Water Outlet Temperature', value: '65 °C' },
      { label: 'Ambient Operating Range', value: '-10 °C to +43 °C' },
      { label: 'Noise Level', value: '37 dB(A)' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Configuration', value: 'Split system (outdoor HP + tank)' },
      { label: 'Country of Origin', value: 'Japan' },
      { label: 'Heritage', value: 'CO2 heat pump pioneer (since 2001)' },
    ],
    warranty: [
      { component: 'Tank', term: '6 years parts*' },
      { component: 'Compressor', term: '6 years parts*' },
      { component: 'Labour', term: 'Per Sanden T&Cs*' },
    ],
    rebateEligible: true,
  },
  'istore-180': {
    slug: 'istore-180',
    category: 'heat-pumps',
    brand: 'istore',
    series: 'iStore Hot Water',
    model: 'iStore 180L',
    name: 'iStore 180L Heat Pump Hot Water',
    image: '/product-images/istore/180l-menu.webp',
    lifestyleImage: '/product-images/istore/banner.jpg',
    heroPills: [
      { icon: 'Zap', label: 'Compact All-in-One' },
      { icon: 'Wifi', label: 'Wi-Fi App' },
      { icon: 'Leaf', label: 'Low-GWP Refrigerant' },
      { icon: 'Volume2', label: 'Quiet Operation' },
    ],
    headlineSpec: { label: 'Tank Size', value: '180', unit: 'L' },
    tagline: 'Australian-designed compact heat pump — perfect for smaller homes and apartments.',
    description:
      "The iStore 180L is a compact all-in-one heat pump hot water system — Australian-designed, with Wi-Fi app control and a robust enamel-coated tank. Ideal for smaller homes, apartments and households with lower hot-water demand. Eligible for federal STCs and state rebates.",
    heroBadge: 'Best for 1–3 person households / apartments',
    keyFeatures: [
      { title: 'Compact 180 L All-in-One', detail: 'Minimal footprint — perfect for smaller spaces.' },
      { title: 'Australian Designed', detail: 'Engineered for Australian conditions.' },
      { title: 'Wi-Fi App Control', detail: 'Remote scheduling and solar-PV alignment.' },
      { title: 'Enamel-Coated Steel Tank', detail: 'Durable, corrosion-resistant tank.' },
      { title: 'Low-GWP Refrigerant', detail: 'Environmentally friendly refrigerant.' },
      { title: 'Quiet Outdoor Operation', detail: 'Low noise output suitable for residential areas.' },
      { title: 'Eligible for STC + State Rebates', detail: 'VEU / ESC / SRES eligible.' },
      { title: 'Part of iStore Ecosystem', detail: 'Pairs with iStore battery + EV charger range.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '180 L' },
      { label: 'Configuration', value: 'All-in-one heat pump water heater' },
      { label: 'Wi-Fi App Control', value: 'Yes' },
      { label: 'Tank Material', value: 'Enamel-coated steel' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Country of Design', value: 'Australia' },
      { label: 'Configuration', value: 'All-in-one (integrated tank + heat pump)' },
      { label: 'Ecosystem', value: 'iStore home electrification range' },
    ],
    warranty: [
      { component: 'Tank', term: 'Per iStore T&Cs*' },
      { component: 'Compressor', term: 'Per iStore T&Cs*' },
      { component: 'Labour', term: 'Per iStore T&Cs*' },
    ],
    rebateEligible: true,
  },
  'istore-270': {
    slug: 'istore-270',
    category: 'heat-pumps',
    brand: 'istore',
    series: 'iStore Hot Water',
    model: 'iStore 270L',
    name: 'iStore 270L Heat Pump Hot Water',
    image: '/product-images/istore/270l-menu.webp',
    lifestyleImage: '/product-images/istore/banner.jpg',
    heroPills: [
      { icon: 'Zap', label: 'All-in-One Compact' },
      { icon: 'Wifi', label: 'Wi-Fi App' },
      { icon: 'Leaf', label: 'Low-GWP Refrigerant' },
      { icon: 'Volume2', label: 'Quiet Operation' },
    ],
    headlineSpec: { label: 'Tank Size', value: '270', unit: 'L' },
    tagline: 'Australian-designed 270L heat pump — the popular choice for typical Aussie family homes.',
    description:
      "The iStore 270L is the more popular sibling — an all-in-one heat pump hot water system sized for typical Australian family homes. Australian-designed, Wi-Fi enabled, with an enamel-coated steel tank and rebate-eligible specifications.",
    heroBadge: 'Best for 4–5 person households',
    keyFeatures: [
      { title: '270 L Tank Capacity', detail: 'Sized for typical Australian family homes.' },
      { title: 'Australian Designed', detail: 'Engineered for Australian conditions.' },
      { title: 'Wi-Fi App Control', detail: 'Remote scheduling and solar-PV alignment.' },
      { title: 'Enamel-Coated Steel Tank', detail: 'Durable, corrosion-resistant tank.' },
      { title: 'Low-GWP Refrigerant', detail: 'Environmentally friendly refrigerant.' },
      { title: 'Quiet Outdoor Operation', detail: 'Low noise output suitable for residential areas.' },
      { title: 'Eligible for STC + State Rebates', detail: 'VEU / ESC / SRES eligible.' },
      { title: 'iStore Ecosystem', detail: 'Pairs with iStore battery + EV charger range.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '270 L' },
      { label: 'Configuration', value: 'All-in-one heat pump water heater' },
      { label: 'Wi-Fi App Control', value: 'Yes' },
      { label: 'Tank Material', value: 'Enamel-coated steel' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Country of Design', value: 'Australia' },
      { label: 'Ecosystem', value: 'iStore home electrification range' },
      { label: 'Best Suited For', value: 'Family homes (4–5 people)' },
    ],
    warranty: [
      { component: 'Tank', term: 'Per iStore T&Cs*' },
      { component: 'Compressor', term: 'Per iStore T&Cs*' },
      { component: 'Labour', term: 'Per iStore T&Cs*' },
    ],
    rebateEligible: true,
  },
  'aquatech-rapid-250': {
    slug: 'aquatech-rapid-250',
    category: 'heat-pumps',
    brand: 'aquatech',
    series: 'Rapid Heat Pump',
    model: 'Aquatech Rapid 250L',
    name: 'Aquatech Rapid 250L Heat Pump',
    image: '/product-images/aquatech/228c32_de03ca3b6ed34607b0f7f9fc4f3ba889.png',
    lifestyleImage: '/product-images/aquatech/228c32_77455d10a51241759093564322608c00.png',
    heroPills: [
      { icon: 'Zap', label: 'Rapid Recovery' },
      { icon: 'Volume2', label: 'Low-Noise' },
      { icon: 'Wifi', label: 'Smart Control' },
      { icon: 'Leaf', label: 'Low-GWP Refrigerant' },
    ],
    headlineSpec: { label: 'Tank Size', value: '250', unit: 'L' },
    tagline: 'Australian hot-water specialists — fast recovery, quiet operation, 250L.',
    description:
      "The Aquatech Rapid 250L is a heat-pump hot water system from one of Australia's veteran water-products specialists. Featuring rapid hot-water recovery, low-noise operation, and rebate-eligible specifications — backed by Aquatech's national service network and decades of hot-water engineering.",
    heroBadge: 'Best for 3–4 person households',
    keyFeatures: [
      { title: 'Rapid Hot-Water Recovery', detail: 'Engineered for quick re-heat times.' },
      { title: 'Low-Noise Operation', detail: 'Inverter compressor for quiet running.' },
      { title: 'Australian Specialists', detail: 'Aquatech has been engineering hot-water products since 1992.' },
      { title: 'Smart Control', detail: 'Wi-Fi-enabled scheduling on select models.' },
      { title: 'Low-GWP Refrigerant', detail: 'Environmentally responsible refrigerant choice.' },
      { title: 'Robust Engineering', detail: 'Commercial-grade build quality.' },
      { title: 'Eligible for STC + State Rebates', detail: 'VEU / ESC / SRES eligible.' },
      { title: 'National Service Network', detail: 'Backed by Aquatech\'s nationwide installer + service support.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '250 L' },
      { label: 'Configuration', value: 'All-in-one heat pump water heater' },
      { label: 'Recovery', value: 'Rapid (variable-speed compressor)' },
      { label: 'Noise Level', value: 'Low-noise inverter operation' },
      { label: 'Smart Control', value: 'Wi-Fi enabled (select models)' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Country of Design', value: 'Australia (since 1992)' },
      { label: 'Best Suited For', value: 'Family homes (3–4 people)' },
    ],
    warranty: [
      { component: 'Tank', term: 'Per Aquatech T&Cs*' },
      { component: 'Compressor', term: 'Per Aquatech T&Cs*' },
      { component: 'Labour', term: 'Per Aquatech T&Cs*' },
    ],
    rebateEligible: true,
  },
  'aquatech-rapid-300': {
    slug: 'aquatech-rapid-300',
    category: 'heat-pumps',
    brand: 'aquatech',
    series: 'Rapid Heat Pump',
    model: 'Aquatech Rapid 300L',
    name: 'Aquatech Rapid 300L Heat Pump',
    image: '/product-images/aquatech/228c32_de03ca3b6ed34607b0f7f9fc4f3ba889.png',
    lifestyleImage: '/product-images/aquatech/228c32_ccf8694eb15143d988a3e40acaa0cee3.png',
    heroPills: [
      { icon: 'Zap', label: 'Rapid Recovery' },
      { icon: 'Volume2', label: 'Low-Noise' },
      { icon: 'Wifi', label: 'Smart Control' },
      { icon: 'Leaf', label: 'Low-GWP Refrigerant' },
    ],
    headlineSpec: { label: 'Tank Size', value: '300', unit: 'L' },
    tagline: 'Larger 300L Rapid heat pump for bigger households — same Australian engineering, more capacity.',
    description:
      "The Aquatech Rapid 300L scales up the same Australian-engineered heat-pump platform for bigger households. Rapid hot-water recovery, low-noise operation, smart control on select models, and full eligibility for federal STCs and state rebates — backed by Aquatech\'s national service network.",
    heroBadge: 'Best for 5–6 person households',
    keyFeatures: [
      { title: '300 L Tank', detail: 'More capacity for larger households or high demand.' },
      { title: 'Rapid Hot-Water Recovery', detail: 'Engineered for quick re-heat times.' },
      { title: 'Low-Noise Operation', detail: 'Inverter compressor for quiet running.' },
      { title: 'Australian Specialists', detail: 'Aquatech — engineering hot-water products since 1992.' },
      { title: 'Smart Control', detail: 'Wi-Fi enabled (select models).' },
      { title: 'Low-GWP Refrigerant', detail: 'Environmentally responsible refrigerant.' },
      { title: 'Robust Engineering', detail: 'Commercial-grade build quality.' },
      { title: 'Eligible for STC + State Rebates', detail: 'VEU / ESC / SRES eligible.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '300 L' },
      { label: 'Configuration', value: 'All-in-one heat pump water heater' },
      { label: 'Recovery', value: 'Rapid (variable-speed compressor)' },
      { label: 'Noise Level', value: 'Low-noise inverter operation' },
      { label: 'Smart Control', value: 'Wi-Fi enabled (select models)' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
      { label: 'Country of Design', value: 'Australia (since 1992)' },
      { label: 'Best Suited For', value: 'Larger family homes (5–6 people)' },
    ],
    warranty: [
      { component: 'Tank', term: 'Per Aquatech T&Cs*' },
      { component: 'Compressor', term: 'Per Aquatech T&Cs*' },
      { component: 'Labour', term: 'Per Aquatech T&Cs*' },
    ],
    rebateEligible: true,
  },
  'stiebel-wwk-222': {
    slug: 'stiebel-wwk-222',
    category: 'heat-pumps',
    brand: 'stiebel',
    series: 'WWK Series',
    model: 'WWK 222 H',
    name: 'Stiebel Eltron WWK 222 (222L)',
    image: '/brand-logos/stiebel.svg',
    lifestyleImage: '/brand-logos/stiebel.svg',
    heroPills: [
      { icon: 'Zap', label: 'German Engineering' },
      { icon: 'Wifi', label: 'Refined Controls' },
      { icon: 'Volume2', label: 'Frost Protection' },
      { icon: 'Leaf', label: 'Low-GWP Refrigerant' },
    ],
    headlineSpec: { label: 'Tank Size', value: '222', unit: 'L' },
    tagline: 'German-engineered integrated heat-pump water heater — precision since 1924.',
    description:
      "The Stiebel Eltron WWK 222 H is a German-engineered integrated heat-pump water heater — combining a high-COP heat pump compressor, refined intuitive controls, frost protection and a corrosion-resistant tank in a single all-in-one design. 222 L capacity suits 3-4 person households.",
    heroBadge: 'Best for 3–4 person households',
    keyFeatures: [
      { title: 'German Engineered', detail: 'Stiebel Eltron has been hot-water specialists since 1924.' },
      { title: 'Integrated Heat-Pump Water Heater', detail: 'All-in-one design — compressor + tank + controls.' },
      { title: 'Refined Intuitive Controls', detail: 'Multi-language LCD display with precision settings.' },
      { title: 'Frost Protection', detail: 'Built-in protection for cold-climate operation.' },
      { title: 'Corrosion-Resistant Tank', detail: 'Long-lasting enamel-coated steel.' },
      { title: 'Low-GWP Refrigerant', detail: 'Environmentally responsible refrigerant choice.' },
      { title: 'Sold in 120+ Countries', detail: 'Global reputation for reliability.' },
      { title: 'Sydney-Based Distributor', detail: 'Australian service network from NSW.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '222 L' },
      { label: 'Configuration', value: 'Integrated heat-pump water heater (all-in-one)' },
      { label: 'Country of Design', value: 'Germany' },
      { label: 'Heritage', value: 'Stiebel Eltron — since 1924' },
      { label: 'Tank Material', value: 'Enamel-coated steel' },
      { label: 'Frost Protection', value: 'Yes (built-in)' },
      { label: 'Controls', value: 'Refined multi-language LCD' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
    ],
    warranty: [
      { component: 'Tank', term: 'Per Stiebel Eltron T&Cs*' },
      { component: 'Compressor', term: 'Per Stiebel Eltron T&Cs*' },
      { component: 'Labour', term: 'Per Stiebel Eltron T&Cs*' },
    ],
    rebateEligible: true,
  },
  'stiebel-wwk-302': {
    slug: 'stiebel-wwk-302',
    category: 'heat-pumps',
    brand: 'stiebel',
    series: 'WWK Series',
    model: 'WWK 302 H',
    name: 'Stiebel Eltron WWK 302 (302L)',
    image: '/brand-logos/stiebel.svg',
    lifestyleImage: '/brand-logos/stiebel.svg',
    heroPills: [
      { icon: 'Zap', label: 'German Engineering' },
      { icon: 'Wifi', label: 'Refined Controls' },
      { icon: 'Volume2', label: '302 L · Large Households' },
      { icon: 'Leaf', label: 'Low-GWP Refrigerant' },
    ],
    headlineSpec: { label: 'Tank Size', value: '302', unit: 'L' },
    tagline: 'Larger 302L WWK heat-pump water heater for bigger households — same German precision engineering.',
    description:
      "The Stiebel Eltron WWK 302 H is the larger sibling — same German-engineered integrated heat-pump water heater platform with 302 L capacity for bigger households. Refined intuitive controls, frost protection, corrosion-resistant tank, all in a single elegant all-in-one design.",
    heroBadge: 'Best for 5–6 person households',
    keyFeatures: [
      { title: '302 L Capacity', detail: 'For larger Australian households.' },
      { title: 'German Engineered', detail: 'Stiebel Eltron — hot-water specialists since 1924.' },
      { title: 'Integrated Heat-Pump Water Heater', detail: 'All-in-one design — compressor + tank + controls.' },
      { title: 'Refined Intuitive Controls', detail: 'Multi-language LCD display with precision settings.' },
      { title: 'Frost Protection', detail: 'Built-in protection for cold-climate operation.' },
      { title: 'Corrosion-Resistant Tank', detail: 'Long-lasting enamel-coated steel.' },
      { title: 'Low-GWP Refrigerant', detail: 'Environmentally responsible refrigerant choice.' },
      { title: 'Sydney-Based Distributor', detail: 'Australian service network from NSW.' },
    ],
    specs: [
      { label: 'Tank Capacity', value: '302 L' },
      { label: 'Configuration', value: 'Integrated heat-pump water heater (all-in-one)' },
      { label: 'Country of Design', value: 'Germany' },
      { label: 'Heritage', value: 'Stiebel Eltron — since 1924' },
      { label: 'Tank Material', value: 'Enamel-coated steel' },
      { label: 'Frost Protection', value: 'Yes (built-in)' },
      { label: 'Controls', value: 'Refined multi-language LCD' },
      { label: 'Voltage / Frequency', value: '220–240 V / 50 Hz' },
    ],
    warranty: [
      { component: 'Tank', term: 'Per Stiebel Eltron T&Cs*' },
      { component: 'Compressor', term: 'Per Stiebel Eltron T&Cs*' },
      { component: 'Labour', term: 'Per Stiebel Eltron T&Cs*' },
    ],
    rebateEligible: true,
  },
  'fox-ess-eq4800': {
    slug: 'fox-ess-eq4800',
    category: 'batteries',
    brand: 'fox-ess',
    series: 'EQ4800 High-Voltage',
    model: 'EQ4800-L2 → L9',
    name: 'Fox ESS EQ4800 (4.7–41.9 kWh)',
    image: '/product-images/fox-ess/eq4800-render.jpg',
    lifestyleImage: '/product-images/fox-ess/battery-hero.jpg',
    heroPills: [
      { icon: 'Zap', label: '100% DOD' },
      { icon: 'Leaf', label: 'LFP Chemistry' },
      { icon: 'Volume2', label: 'IP65 Outdoor' },
      { icon: 'Wifi', label: 'CAN Comms' },
    ],
    headlineSpec: {
      label: 'Energy',
      value: '4.7',
      unit: 'kWh',
    },
    tagline: 'Compact high-voltage modular battery scalable from 4.7 to 41.9 kWh.',
    description:
      'The Fox ESS EQ4800 is a high-voltage modular home battery built on LFP (LiFePO4) cells with 100% usable depth-of-discharge. Stack from 2 to 9 modules to reach 9.32–41.93 kWh, with IP65 outdoor-rated enclosures, plug-and-play install, and CAN-bus communication. Designed for compact-to-medium Australian homes pairing with single-phase hybrid inverters.',
    installationImages: [
      '/product-images/fox-ess/eq4800-install-1.jpg',
      '/product-images/fox-ess/eq4800-install-2.jpg',
      '/product-images/fox-ess/eq4800-install-3.jpg',
      '/product-images/fox-ess/eq4800-install-4.jpg',
    ],
    installationCaption:
      'Real Fox ESS EQ4800 installations — wall-mounted hybrid inverters paired with stacked LFP modules in Australian garages and external alcoves.',
    heroBadge: 'Best for compact to medium households',
    keyFeatures: [
      { title: 'High Voltage Architecture', detail: 'Stacked-cell topology with up to 463.7 V DC for lower current loss and high efficiency.' },
      { title: 'LFP (LiFePO4) Chemistry', detail: 'Industry-safest lithium chemistry — thermally stable, long cycle life.' },
      { title: '100% Depth-of-Discharge', detail: 'Use every kWh — no derated usable capacity.' },
      { title: 'Modular Scalability', detail: 'Stack 2 to 9 modules — 9.32 kWh up to 41.93 kWh.' },
      { title: 'IP65 Ingress Protection', detail: 'Outdoor-rated for flexible install locations.' },
      { title: 'Plug & Play Install', detail: 'Modular design with simple mechanical and electrical connections.' },
      { title: 'High Round-Trip Efficiency', detail: '>95% round-trip — more solar self-consumption banked.' },
      { title: 'Wide Operating Range', detail: '-10 °C to +55 °C discharge — built for Australian climates.' },
    ],
    specs: [
      { label: 'Cell Chemistry', value: 'LFP (LiFePO4)' },
      { label: 'Nominal Energy (L2–L9)', value: '9.32 – 41.93 kWh' },
      { label: 'Usable Energy', value: '100% of nominal' },
      { label: 'Nominal Voltage Range', value: '89.6 – 403.2 V DC' },
      { label: 'Operating Voltage Range', value: '81.2 – 463.7 V DC' },
      { label: 'Charge / Discharge Current', value: '30 A nominal · 50 A max' },
      { label: 'Charge / Discharge Power', value: '4.48 – 20.16 kW (L2 → L9)' },
      { label: 'Depth of Discharge', value: '100%' },
      { label: 'Round-Trip Efficiency', value: '>95%' },
      { label: 'Ingress Protection', value: 'IP65' },
      { label: 'Operating Temperature', value: 'Charge: 0 °C to 55 °C · Discharge: −10 °C to 55 °C' },
      { label: 'Communication', value: 'CAN bus' },
      { label: 'Module Dimensions', value: '570 × 386–1352 × 380 mm (depending on stack)' },
      { label: 'Weight (L2 → L9)', value: '83.5 – 356.5 kg' },
      { label: 'Certifications', value: 'IEC 62619, UN38.3, IEC 61000-6-1/2/3/4' },
    ],
    warranty: [
      { component: 'Product', term: 'Per Fox ESS T&Cs (registration required)*' },
      { component: 'Performance', term: 'Per Fox ESS T&Cs*' },
      { component: 'Labour', term: 'Per Supa Energy Hub install agreement*' },
    ],
    rebateEligible: true,
  },
  'fox-ess-cq6': {
    slug: 'fox-ess-cq6',
    category: 'batteries',
    brand: 'fox-ess',
    series: 'CQ6 High-Voltage',
    model: 'CQ6-L2 → L14',
    name: 'Fox ESS CQ6 (12–83.9 kWh)',
    image: '/product-images/fox-ess/cq6-render.jpg',
    lifestyleImage: '/product-images/fox-ess/battery-keyadv.jpg',
    heroPills: [
      { icon: 'Zap', label: '100% DOD' },
      { icon: 'Leaf', label: 'LFP Chemistry' },
      { icon: 'Volume2', label: 'IP65 Outdoor' },
      { icon: 'Wifi', label: 'CAN Comms' },
    ],
    headlineSpec: {
      label: 'Energy',
      value: '12',
      unit: 'kWh',
    },
    tagline: 'High-voltage family-home battery — scalable from 12 to 83.9 kWh.',
    description:
      'The Fox ESS CQ6 is a high-voltage modular battery for medium-to-large Australian homes. Built on LFP cells with 100% usable depth-of-discharge, the CQ6 stacks from 2 to 14 modules for 11.98 kWh up to 83.86 kWh. IP65 outdoor-rated, with up to 50 A charge/discharge current and CAN-bus communication for tight inverter integration.',
    heroBadge: 'Best for medium to large family homes',
    keyFeatures: [
      { title: 'High Voltage Architecture', detail: 'Up to 919.8 V DC stack voltage for efficient long-cable runs.' },
      { title: 'LFP (LiFePO4) Chemistry', detail: 'Safest mainstream lithium chemistry with long cycle life.' },
      { title: '100% Depth-of-Discharge', detail: 'Every stored kWh is usable.' },
      { title: 'Large-Home Scalability', detail: 'Stack 2 to 14 modules — 11.98 kWh up to 83.86 kWh.' },
      { title: 'IP65 Ingress Protection', detail: 'Outdoor-rated for garage, side passage or external walls.' },
      { title: 'Plug & Play Modules', detail: 'Mechanical stack-and-clip with sealed power connectors.' },
      { title: 'High Round-Trip Efficiency', detail: '>95% — minimises stored-energy losses.' },
      { title: 'Wide Operating Range', detail: '−10 °C to 55 °C discharge envelope.' },
    ],
    specs: [
      { label: 'Cell Chemistry', value: 'LFP (LiFePO4)' },
      { label: 'Nominal Energy (L2–L14)', value: '11.98 – 83.86 kWh' },
      { label: 'Single Module Energy', value: '5.99 kWh' },
      { label: 'Usable Energy', value: '100% of nominal' },
      { label: 'Nominal Voltage Range', value: '115.2 – 806.4 V DC' },
      { label: 'Operating Voltage Range', value: '104.4 – 919.8 V DC' },
      { label: 'Charge / Discharge Current', value: 'Up to 50 A' },
      { label: 'Charge / Discharge Power', value: '5.76 – 40.32 kW (L2 → L14)' },
      { label: 'Depth of Discharge', value: '100%' },
      { label: 'Round-Trip Efficiency', value: '>95%' },
      { label: 'Ingress Protection', value: 'IP65' },
      { label: 'Operating Temperature', value: 'Charge: 0 °C to 55 °C · Discharge: −10 °C to 55 °C · Storage: −10 °C to 50 °C' },
      { label: 'Communication', value: 'CAN bus' },
      { label: 'Module Dimensions (W × D × H)', value: '420–2280 × 360 × 660 mm' },
      { label: 'Weight (L2 → L14)', value: '105.2 – 705.2 kg' },
      { label: 'Certifications', value: 'IEC 62619, UN38.3, IEC 61000-6-1/2/3/4' },
    ],
    warranty: [
      { component: 'Product', term: 'Per Fox ESS T&Cs (registration required)*' },
      { component: 'Performance', term: 'Per Fox ESS T&Cs*' },
      { component: 'Labour', term: 'Per Supa Energy Hub install agreement*' },
    ],
    rebateEligible: true,
  },
  'fox-ess-cq7': {
    slug: 'fox-ess-cq7',
    category: 'batteries',
    brand: 'fox-ess',
    series: 'CQ7 High-Voltage',
    model: 'CQ7-L2 → L14',
    name: 'Fox ESS CQ7 (13.9–97.4 kWh)',
    image: '/product-images/fox-ess/cq7-render.jpg',
    lifestyleImage: '/product-images/fox-ess/battery-bg.jpg',
    heroPills: [
      { icon: 'Zap', label: '80 A Current' },
      { icon: 'Leaf', label: 'LFP Chemistry' },
      { icon: 'Volume2', label: 'IP65 Outdoor' },
      { icon: 'Wifi', label: 'CAN Comms' },
    ],
    headlineSpec: {
      label: 'Energy',
      value: '14',
      unit: 'kWh',
    },
    tagline: 'Flagship CQ7 — 80 A high-current battery, scalable to 97.4 kWh.',
    description:
      'The Fox ESS CQ7 is the flagship high-voltage modular battery — a step up from CQ6 with a higher 80 A nominal current and an optional cold-weather warm-up function (−25 °C tolerance). Scalable from 2 to 14 modules for 13.92 kWh to 97.44 kWh, the CQ7 suits large family homes, three-phase loads and homes preparing for EV charging.',
    heroBadge: 'Best for large homes and high-load installations',
    keyFeatures: [
      { title: '80 A High-Current Design', detail: 'Higher nominal current rating than CQ6 — supports larger inverter loads.' },
      { title: 'LFP (LiFePO4) Chemistry', detail: 'Safest mainstream lithium chemistry with long cycle life.' },
      { title: 'Optional Cold-Climate Warm-Up', detail: 'CQ7(w) variant — usable down to −25 °C ambient.' },
      { title: '100% Depth-of-Discharge', detail: 'Every kWh stored is usable.' },
      { title: 'Massive Scalability', detail: 'Stack 2 to 14 modules — 13.92 kWh up to 97.44 kWh.' },
      { title: 'IP65 Ingress Protection', detail: 'Outdoor-rated for flexible install locations.' },
      { title: 'High Round-Trip Efficiency', detail: '>95% — minimises stored-energy losses.' },
      { title: 'Plug & Play Modules', detail: 'Modular stack design with sealed power connectors.' },
    ],
    specs: [
      { label: 'Cell Chemistry', value: 'LFP (LiFePO4)' },
      { label: 'Nominal Energy', value: '13.92 – 97.44 kWh' },
      { label: 'Usable Energy', value: '100% of nominal' },
      { label: 'Nominal Voltage', value: '115.2 V DC (per module)' },
      { label: 'Operating Voltage Range (stack)', value: '104.4 – 919.8 V DC' },
      { label: 'Nominal Charge / Discharge Current', value: '80 A' },
      { label: 'Depth of Discharge', value: '100%' },
      { label: 'Round-Trip Efficiency', value: '>95%' },
      { label: 'Ingress Protection', value: 'IP65' },
      { label: 'Operating Temperature (Charge)', value: '0 °C to 55 °C' },
      { label: 'Operating Temperature (Discharge)', value: '−10 °C to 55 °C (CQ7(w): −25 °C to 55 °C with warm-up)' },
      { label: 'Storage Temperature', value: '−10 °C to 50 °C' },
      { label: 'Communication', value: 'CAN bus' },
      { label: 'Module Dimensions (W × D × H)', value: '420–2280 × 360 × 660 mm' },
      { label: 'Weight', value: '105.5 – 711.5 kg' },
      { label: 'Certifications', value: 'IEC 62619, UN38.3, IEC 61000-6-1/2/3/4' },
    ],
    warranty: [
      { component: 'Product', term: 'Per Fox ESS T&Cs (registration required)*' },
      { component: 'Performance', term: 'Per Fox ESS T&Cs*' },
      { component: 'Labour', term: 'Per Supa Energy Hub install agreement*' },
    ],
    rebateEligible: true,
  },
  'whes-agave-sh': {
    slug: 'whes-agave-sh',
    category: 'batteries',
    brand: 'whes',
    series: 'AGAVE-SH Ultra (Single-Phase)',
    model: 'WH-SIA 3.6 → 10 kW (BXC4992)',
    name: 'WHES AGAVE-SH Ultra (3.6–10 kW · 5–19.9 kWh)',
    image: '/product-images/whes/agave-sh-render.png',
    lifestyleImage: '/product-images/whes/agave-th-hero.png',
    imageBlendMode: 'normal',
    heroStatCard: {
      label: 'Manufacturer Warranty',
      value: '10',
      unit: '-yr',
      sub: 'Product + performance · 70% capacity*',
      icon: 'Shield',
    },
    heroChip: { label: 'LFP Safe', icon: 'Leaf' },
    heroPills: [
      { icon: 'Zap', label: '3.6–10 kW Hybrid' },
      { icon: 'Leaf', label: 'LFP Chemistry' },
      { icon: 'Volume2', label: 'IP65 · <25 dB' },
      { icon: 'Shield', label: 'Built-in Fire Kit' },
    ],
    headlineSpec: {
      label: 'Energy',
      value: '5',
      unit: 'kWh',
    },
    tagline: 'Single-phase all-in-one — hybrid inverter, LFP battery, EMS and fire-suppression in one 600 mm cabinet.',
    description:
      'The WHES AGAVE-SH Ultra is a single-phase residential All-In-One Energy Storage System. Six inverter variants (3.6 / 4.6 / 5 / 6 / 8 / 10 kW) integrate with 1–4 stackable BXC4992 LFP battery modules (4.992 kWh each) for a 4.99 to 19.97 kWh usable range. Supports up to 5 units in parallel for installations up to ~99.84 kWh. Built-in meter and plug-in terminals save up to 75% install time, and a built-in fire-suppression kit plus four-layer safety design make it one of the safest single-phase ESS cabinets on the Australian market.',
    brochureUrl: '/brochures/whes/whes-agave-sh-datasheet.pdf',
    warrantyUrl: '/brochures/whes/whes-residential-warranty-au.pdf',
    installationCaption:
      'Compact 600 mm-wide single-phase WHES Power Pod cabinets — wall-mounted with the inverter, BXC4992 LFP modules, and integrated EMS in one elegant enclosure.',
    heroBadge: 'Best for single-phase homes (most Australian properties)',
    keyFeatures: [
      { title: 'All-In-One Cabinet', detail: 'Hybrid inverter, LFP battery modules, EMS and fire-suppression in a single 600 × 350 mm enclosure.' },
      { title: 'LFP (LiFePO4) Chemistry', detail: 'UL 9540A-certified cells — safest mainstream lithium chemistry.' },
      { title: 'BXC4992 Modular Battery', detail: '4.992 kWh per module · 1 to 4 modules per cabinet (4.99 to 19.97 kWh).' },
      { title: 'Six Power Variants', detail: 'SIA-3.6 / 4.6 / 5 / 6 / 8 / 10 kW — sized to single-phase home loads and solar systems up to 20 kWp.' },
      { title: 'Fast EPS Backup', detail: '<10 ms switch-over — keeps essential circuits running through outages.' },
      { title: '75% Faster Install', detail: 'Plug-in terminals replace manual wiring between modules — 15-min stackable install.' },
      { title: 'Whisper Quiet', detail: '<25 dB ambient noise — quieter than a library, safe near bedrooms.' },
      { title: 'Built-In Fire Kit', detail: 'Intelligent fire-suppression kit identifies and extinguishes fires within 15 seconds.' },
    ],
    specs: [
      { label: 'System Type', value: 'Single-Phase All-In-One ESS' },
      { label: 'Inverter Power (Rated)', value: '3.6 / 4.6 / 5 / 6 / 8 / 10 kW (six variants)' },
      { label: 'Max DC Input Power', value: 'Up to 20 kWp (SIA-10kW)' },
      { label: 'Max PV Input Voltage', value: '600 V DC' },
      { label: 'MPPT Voltage Range', value: '50 – 560 V' },
      { label: 'MPP Trackers / Strings', value: '2 MPPT · up to 2 strings per tracker' },
      { label: 'AC Output Voltage', value: '220 / 230 / 240 V (1/N/PE)' },
      { label: 'Rated Frequency', value: '50 / 60 Hz' },
      { label: 'Battery Module', value: 'WH-BXC4992 · 4.992 kWh · LFP' },
      { label: 'Battery Modules / Unit', value: '1 to 4 (4.99 – 19.97 kWh)' },
      { label: 'Battery Voltage Range', value: '80 – 500 V DC' },
      { label: 'Max PV Efficiency', value: '97.8% (97.3% Euro)' },
      { label: 'EPS Switch-Over Time', value: '<10 ms' },
      { label: 'Parallel Capability', value: 'Up to 5 units (~99.84 kWh)' },
      { label: 'Ingress Protection', value: 'IP65' },
      { label: 'Operating Temperature', value: '−20 °C to +55 °C (with optional heating module)' },
      { label: 'Noise Emission', value: '<25 dB' },
      { label: 'Dimensions (W × D × H)', value: '600 × 350 × 1800 mm (4-module unit)' },
      { label: 'Battery Module Weight', value: '57 kg' },
      { label: 'Communication', value: 'RS485, CAN' },
      { label: 'Certifications', value: 'AS/NZS 4777.2, UN 38.3, IEC 62619, IEC 63056, IEC 62109, IEC 62040, UL9540A' },
    ],
    warranty: [
      { component: 'Product', term: '10 years*' },
      { component: 'Performance', term: '10 years @ 70% usable energy*' },
      { component: 'Accessories', term: '2 years (CT, Smart Meter, WiFi)*' },
    ],
    rebateEligible: true,
  },
  'whes-agave-th': {
    slug: 'whes-agave-th',
    category: 'batteries',
    brand: 'whes',
    series: 'AGAVE-TH (Three-Phase)',
    model: 'WH-TIA 5 → 13 kW (BXC4992)',
    name: 'WHES AGAVE-TH (5–13 kW · 9.98–29.94 kWh)',
    image: '/product-images/whes/agave-th-render.png',
    lifestyleImage: '/product-images/whes/agave-th-hero.png',
    imageBlendMode: 'normal',
    heroStatCard: {
      label: 'Manufacturer Warranty',
      value: '10',
      unit: '-yr',
      sub: 'Product + performance · 70% capacity*',
      icon: 'Shield',
    },
    heroChip: { label: 'LFP Safe', icon: 'Leaf' },
    heroPills: [
      { icon: 'Zap', label: '5–13 kW · 3-Phase' },
      { icon: 'Leaf', label: 'LFP Chemistry' },
      { icon: 'Volume2', label: 'IP65 · <25 dB' },
      { icon: 'Shield', label: 'Built-in Fire Kit' },
    ],
    headlineSpec: {
      label: 'Energy',
      value: '10',
      unit: 'kWh',
    },
    tagline: 'Three-phase all-in-one — 110% unbalanced output, 5–13 kW hybrid inverter, up to 29.94 kWh per cabinet.',
    description:
      'The WHES AGAVE-TH is a three-phase residential All-In-One Energy Storage System for larger Australian homes on three-phase supply. Six inverter variants (5 / 6 / 8 / 10 / 12 / 13 kW) integrate with 2–6 stackable BXC4992 LFP battery modules (4.992 kWh each) for 9.98 to 29.94 kWh per cabinet. Supports up to 5 units in parallel for up to 149.76 kWh total, 110% three-phase unbalanced output (great for solar over-configuration), and 2× PV over-sizing up to 20 kWp.',
    brochureUrl: '/brochures/whes/whes-agave-th-datasheet.pdf',
    warrantyUrl: '/brochures/whes/whes-residential-warranty-au.pdf',
    installationCaption:
      'Three-phase WHES Power Pod cabinets — purpose-built for Australian three-phase residential supplies with 110% unbalanced output and up to 30 kWh per unit.',
    heroBadge: 'Best for three-phase homes and high-export PV systems',
    keyFeatures: [
      { title: 'Three-Phase All-In-One', detail: 'Inverter, LFP modules, EMS and fire-kit in a single cabinet — designed for 3-phase residential supply.' },
      { title: '110% Unbalanced Output', detail: 'Handles uneven per-phase loads better than typical 3-phase inverters.' },
      { title: '2× PV Over-Configuration', detail: 'DC input up to 20 kWp per cabinet — generous solar headroom.' },
      { title: 'BXC4992 Modular Battery', detail: '4.992 kWh per module · 2 to 6 modules per cabinet (9.98 to 29.94 kWh).' },
      { title: 'Six Power Variants', detail: 'WH-TIA 5 / 6 / 8 / 10 / 12 / 13 kW — sized to three-phase home loads.' },
      { title: 'Fast EPS Backup', detail: '<10 ms switch-over for whole-home backup circuits.' },
      { title: 'Whisper Quiet', detail: '<25 dB ambient noise — install anywhere, even near bedrooms.' },
      { title: 'Built-In Fire Kit', detail: 'Intelligent fire-suppression kit extinguishes fires within 15 seconds.' },
    ],
    specs: [
      { label: 'System Type', value: 'Three-Phase All-In-One ESS' },
      { label: 'Inverter Power (Rated)', value: '5 / 6 / 8 / 10 / 12 / 13 kW (six variants)' },
      { label: 'Max DC Input Power', value: 'Up to 20 kWp' },
      { label: 'Absolute Max DC Voltage', value: '1000 V DC' },
      { label: 'MPPT Voltage Range', value: '180 – 980 V' },
      { label: 'MPP Trackers / Strings', value: '2 MPPT · 1–2 strings per tracker' },
      { label: 'AC Output Voltage', value: '3/N/PE · 220/380 V · 230/400 V · 240/415 V' },
      { label: 'Three-Phase Unbalanced Output', value: 'Up to 110%' },
      { label: 'Battery Module', value: 'WH-BXC4992 · 4.992 kWh · LFP' },
      { label: 'Battery Modules / Unit', value: '2 to 6 (9.98 – 29.94 kWh)' },
      { label: 'Battery Voltage Range', value: '160 – 700 V DC' },
      { label: 'EPS Switch-Over Time', value: '<10 ms' },
      { label: 'Parallel Capability', value: 'Up to 5 units (~149.76 kWh)' },
      { label: 'Ingress Protection', value: 'IP65' },
      { label: 'Operating Temperature', value: '−20 °C to +55 °C (with heating module)' },
      { label: 'Noise Emission', value: '<25 dB' },
      { label: 'Dimensions (W × D × H)', value: '600 × 350 × 1880 mm (four-module unit with foundation)' },
      { label: 'Unit Weight', value: '33 kg (without battery modules)' },
      { label: 'Communication', value: 'LED / APP / RS485 / CAN' },
      { label: 'Certifications', value: 'AS/NZS 4777.2, EN 50549-1, UN 38.3, IEC 62619, IEC 63056, IEC 62109, IEC 62040, UL9540A' },
    ],
    warranty: [
      { component: 'Product', term: '10 years*' },
      { component: 'Performance', term: '10 years @ 70% usable energy*' },
      { component: 'Accessories', term: '2 years (CT, Smart Meter, WiFi)*' },
    ],
    rebateEligible: true,
  },
  'ecactus-agave-sh-10': {
    slug: 'ecactus-agave-sh-10',
    category: 'batteries',
    brand: 'ecactus',
    series: 'Agave SH 10.24',
    model: 'WH-SPHA5.0H-10.24kWh',
    name: 'eCactus Agave SH (5 kW · 10.24 kWh)',
    image: '/product-images/whes/agave-sh-render.png',
    lifestyleImage: '/product-images/whes/agave-th-hero.png',
    imageBlendMode: 'normal',
    heroVariant: 'light',
    heroStatCard: {
      label: 'Manufacturer Warranty',
      value: '10',
      unit: '-yr',
      sub: 'Product + performance · 70% capacity*',
      icon: 'Shield',
    },
    heroChip: { label: 'LFP Safe', icon: 'Leaf' },
    heroPills: [
      { icon: 'Zap', label: '5 kW Hybrid' },
      { icon: 'Leaf', label: 'LFP Chemistry' },
      { icon: 'Volume2', label: 'IP65 · <25 dB' },
      { icon: 'Shield', label: 'Built-in Fire Kit' },
    ],
    headlineSpec: { label: 'Energy', value: '10.24', unit: 'kWh' },
    tagline: 'Single-phase all-in-one — 5 kW hybrid inverter + 10.24 kWh LFP stack in one IP65 cabinet.',
    description:
      'The eCactus Agave SH (WH-SPHA5.0H-10.24kWh) is a single-phase residential all-in-one battery storage system. A 5 kW hybrid inverter, LFP battery modules and EMS integrate into a single 600 mm-wide IP65 cabinet — 10.24 kWh per stack, scalable up to 5 cabinets in parallel (~51 kWh). Built-in fire-suppression kit, four-layer safety design, <10 ms EPS backup switch-over, <25 dB ambient noise.',
    brochureUrl: '/brochures/ecactus/ecactus-agave-sh-datasheet.pdf',
    warrantyUrl: '/brochures/ecactus/ecactus-aio-warranty-au.pdf',
    heroBadge: 'Best for single-phase homes — most Australian properties',
    keyFeatures: [
      { title: 'All-in-One Cabinet', detail: 'Hybrid inverter, LFP modules, EMS and fire-kit in one 600 × 350 mm enclosure.' },
      { title: 'LFP (LiFePO4) Chemistry', detail: 'UL 9540A-certified cells — safest mainstream lithium chemistry.' },
      { title: '10.24 kWh per Stack', detail: 'Scalable up to 5 cabinets in parallel for ~51 kWh of usable storage.' },
      { title: '5 kW Hybrid Inverter', detail: 'Sized to typical single-phase home loads and solar systems up to ~10 kWp.' },
      { title: 'Fast EPS Backup', detail: '<10 ms switch-over — keeps essential circuits running through outages.' },
      { title: '75% Faster Install', detail: 'Plug-in terminals replace manual wiring between modules — 15-min stackable install.' },
      { title: 'Whisper Quiet', detail: '<25 dB ambient noise — safe to install near bedrooms.' },
      { title: 'Built-In Fire Kit', detail: 'Intelligent fire-suppression kit extinguishes fires within 15 seconds.' },
    ],
    specs: [
      { label: 'System Type', value: 'Single-Phase All-In-One ESS' },
      { label: 'Inverter Power (Rated)', value: '5 kW' },
      { label: 'Battery Module', value: 'WH-BXC4992 · 4.992 kWh · LFP' },
      { label: 'Stack Energy', value: '10.24 kWh (2 modules)' },
      { label: 'Parallel Capability', value: 'Up to 5 cabinets (~51 kWh total)' },
      { label: 'EPS Switch-Over Time', value: '<10 ms' },
      { label: 'Max PV Input Voltage', value: '600 V DC' },
      { label: 'MPPT Voltage Range', value: '50 – 560 V' },
      { label: 'MPP Trackers / Strings', value: '2 MPPT · up to 2 strings per tracker' },
      { label: 'AC Output Voltage', value: '220 / 230 / 240 V (1/N/PE)' },
      { label: 'Rated Frequency', value: '50 / 60 Hz' },
      { label: 'Max PV Efficiency', value: '97.8%' },
      { label: 'Ingress Protection', value: 'IP65' },
      { label: 'Operating Temperature', value: '−20 °C to +55 °C' },
      { label: 'Noise Emission', value: '<25 dB' },
      { label: 'Communication', value: 'RS485, CAN' },
      { label: 'Certifications', value: 'AS/NZS 4777.2, UN 38.3, IEC 62619/63056/62109/62040, UL9540A' },
    ],
    warranty: [
      { component: 'Product', term: '10 years*' },
      { component: 'Performance', term: '10 years @ 70% usable energy*' },
      { component: 'Accessories', term: '2 years (CT, Smart Meter, WiFi)*' },
    ],
    rebateEligible: true,
  },
};

// Helpers
export const getProductsByCategory = (categorySlug) =>
  Object.values(products).filter((p) => p.category === categorySlug);

export const getProductsByBrand = (brandSlug) =>
  Object.values(products).filter((p) => p.brand === brandSlug);

export const getProduct = (slug) => products[slug];
export const getBrand = (slug) => brands[slug];
export const getCategory = (slug) => categories[slug];
